export const commonFaqs = [
  {
    question: 'Can I select a specific design style for my room?',
    answer:
      'Yes, you can choose from 30+ pre-defined styles including Modern, Scandinavian, Industrial, Minimalist, Bohemian, Traditional, Contemporary, Rustic, Coastal, Mediterranean, Farmhouse, Mid-Century Modern, Art Deco, Gothic, Japanese Zen, Moroccan, French Country, Shabby Chic, Hollywood Glamour, and many more. You can also select "Custom" and add your own style details in the prompt for a truly personalized design that matches your unique vision and preferences.',
  },
  {
    question: 'Can I generate a video of my room design?',
    answer:
      'Yes! After generating your design, you can create immersive videos that showcase your new space from multiple angles. You can customize the experience with prompts like "zoom in on the fireplace," "pan across the room," "fly over the furniture layout," "focus on lighting effects," or other specific camera movements to highlight different aspects of your design. These videos help you visualize the space in action and share your design concepts with others.',
  },
  {
    question: 'Do I need to upload a photo of my existing room?',
    answer:
      'No, uploading a photo isn\'t necessary if you want to generate new ideas from scratch. If you do have a photo, you can use "Restyle" mode where our AI will maintain the room\'s structure, architectural features, and dimensions while applying new design elements, colors, and furniture. "Generate" mode is ideal for planning new spaces, completely reimagining existing ones, or exploring creative possibilities without being constrained by current layouts.',
  },
  {
    question: 'How many designs can I generate at once?',
    answer:
      'You can generate up to 10 different style variations in a single session. Each variation offers unique combinations of colors, furniture arrangements, lighting, and decor elements. After reviewing these options, you can select another style category or regenerate to explore even more creative possibilities. This approach allows you to compare different design approaches, identify your favorites, and find the perfect aesthetic for your space.',
  },
  {
    question: 'How accurate are the AI-generated room designs?',
    answer:
      'Our advanced AI generates highly realistic designs while precisely respecting your original room dimensions, architectural features, window placements, and structural elements. The results provide an excellent visualization of how different styles would look in your actual space, with accurate lighting effects, material textures, and spatial relationships. While some creative interpretation is applied for aesthetic enhancement, all designs maintain dimensional accuracy and practical functionality.',
  },
  {
    question:
      'What makes Easy-Peasy.ai different from other room design tools?',
    answer:
      'Easy-Peasy.ai combines cutting-edge AI technology with practical design expertise to deliver professional-quality results. Our system understands spatial relationships, lighting effects, color theory, and current design trends. Unlike basic apps, we provide detailed style variations, video walkthroughs, and designs that work within your actual room constraints. Plus, our extensive style library and customization options ensure you get designs that truly reflect your personal taste and lifestyle needs.',
  },
  {
    question:
      'Can I use the designs for professional projects or commercial spaces?',
    answer:
      'Absolutely! Our AI generates high-quality designs suitable for residential, commercial, and professional applications. Interior designers, real estate professionals, architects, and homeowners alike use our platform to create stunning visualizations for client presentations, marketing materials, renovation projects, and new construction. All designs can be exported and used for professional purposes with proper attribution to our platform.',
  },

  {
    question: 'How to choose the best AI room design tool for your needs',
    answer:
      'Consider ease of use, style variety (look for 30+ options), accuracy in maintaining room dimensions, video generation capabilities, export options for professional use, customer support quality, and pricing structure. Easy-Peasy.ai excels in all these areas with our comprehensive style library, precise dimensional accuracy, immersive video features, and dedicated support for both homeowners and professionals.',
  },
  {
    question: 'Color psychology and mood-enhancing room design',
    answer:
      'Blue promotes calm and productivity (ideal for offices), green creates relaxation and reduces stress (perfect for bedrooms), yellow stimulates creativity and energy (great for kitchens), warm neutrals provide comfort and sophistication (living rooms), and consider natural light interaction with paint colors throughout the day.',
  },
  {
    question: 'Universal design principles for aging in place',
    answer:
      'Incorporate zero-step entries, wide doorways (36" minimum), lever-style door handles, accessible storage at reachable heights, non-slip flooring, adequate lighting, and consider future needs like grab bars and reinforced walls. Design for longevity and adaptability as needs change.',
  },
  {
    question: 'How to maintain and update room designs over time',
    answer:
      'Plan for flexible elements like changeable accent walls, modular furniture, adjustable lighting systems, and timeless color palettes. Set up a maintenance schedule for fabrics and finishes, keep original design files for future updates, and consider seasonal refreshes with new textiles and accessories.',
  },
];

export const roomTypesList: {
  slug: string;
  name: string;
  faqs?: Array<{
    question: string;
    answer: string;
  }>;
  /**
   * Optional animated hero (muted, looping mp4) shown instead of the static
   * `room-types/<slug>.jpeg`, which stays as the poster + SEO image. The clip
   * should be a before -> after transformation of ONE room, not a standalone
   * pretty render: this page sells "your room, redesigned", and a lone finished
   * room reads as a stock photo.
   */
  heroVideo?: string;
}[] = [
  {
    slug: 'living-room',
    name: 'Living Room',
    faqs: [
      {
        question: 'What are the best furniture layouts for small living rooms?',
        answer:
          'For small living rooms, focus on multi-functional furniture, use smaller scale pieces, create conversation areas, and consider floating furniture away from walls to create a sense of space.',
      },
      {
        question: 'How do I choose the right color scheme for my living room?',
        answer:
          'Consider natural lighting, room size, existing elements like flooring, your personal style preferences, and current color trends. Neutral colors provide flexibility, while accent colors can add personality.',
      },
      {
        question: 'What lighting fixtures work best for living rooms?',
        answer:
          'A layered lighting approach works best: ambient lighting (ceiling fixtures, recessed lights), task lighting (reading lamps, under-cabinet lights), and accent lighting (wall sconces, picture lights) to create a versatile and comfortable space.',
      },
      {
        question: 'How to redesign a living room on a $2000 budget?',
        answer:
          'Focus on affordable paint ($300 for 2-3 gallons), thrifted or discounted furniture ($800-1000 for sofa and chairs), DIY decor projects ($400 for pillows, curtains, and wall art), and smart LED lighting ($500 for fixtures and bulbs). Prioritize quality basics like comfortable seating and good lighting over trendy accessories.',
      },
      {
        question: 'Best living room color schemes that never go out of style',
        answer:
          'Choose timeless neutrals like warm beige walls (Sherwin Williams Agreeable Gray), soft white trim, and natural wood tones for furniture. Add personality with accent pillows in navy blue or forest green. For a sophisticated look, consider a monochromatic scheme with varying shades of gray, or an earthy palette with terracotta and sage green.',
      },
      {
        question: 'How to integrate smart home devices into living room design',
        answer:
          'Position smart speakers near seating areas for voice control, install smart lighting switches for ambiance control, mount smart displays on walls for entertainment, hide smart plugs behind furniture for device charging, and consider motorized blinds for automated light control. Ensure all devices work together through a unified smart home system.',
      },
      {
        question:
          'Sustainable materials for eco-friendly living room renovation',
        answer:
          'Choose bamboo or reclaimed wood flooring, organic cotton or linen upholstery, recycled glass or cork accent tables, natural fiber rugs (wool or sisal), and low-VOC paints. Look for FSC-certified wood products and furniture made from recycled materials. Consider energy-efficient LED lighting and solar-powered smart devices.',
      },
      {
        question: 'How to create a reading nook in your living room',
        answer:
          'Dedicate a corner or alcove with a comfortable armchair, add a small bookshelf within reach, install adjustable task lighting, include a side table for books and beverages, add soft textiles like a throw blanket and pillows, and use a room divider or tall plants to create separation from the main space.',
      },
      {
        question: 'Living room layout ideas for entertaining large groups',
        answer:
          'Arrange seating in a U-shape or semi-circle around a central focal point like a fireplace or TV. Use modular furniture that can be rearranged, include multiple conversation areas, ensure clear pathways between seating groups, add a bar cart or sideboard for serving, and consider folding chairs that can be stored when not needed.',
      },
      {
        question: 'Best flooring options for high-traffic living rooms',
        answer:
          'Hardwood flooring offers durability and classic appeal, luxury vinyl plank (LVP) provides water resistance and easy maintenance, carpet tiles allow for easy replacement of worn areas, and natural stone like travertine adds luxury while being scratch-resistant. Consider traffic patterns and choose materials rated for heavy use.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'bedroom',
    name: 'Bedroom',
    faqs: [
      {
        question: 'How should I position my bed in the bedroom?',
        answer:
          'Ideally, position your bed against a solid wall, not under windows or directly facing the door. Consider feng shui principles by having the headboard against a wall while allowing access from both sides of the bed.',
      },
      {
        question: 'What are essential elements for a restful bedroom design?',
        answer:
          'A restful bedroom includes a quality mattress, blackout curtains or blinds, proper temperature control, minimal electronic devices, a calming color palette, adequate storage to minimize clutter, and layered lighting options.',
      },
      {
        question: 'How can I maximize storage in a small bedroom?',
        answer:
          'Utilize under-bed storage containers, choose a bed with built-in drawers, use vertical space with tall dressers or shelving, install closet organizers, and consider multifunctional furniture like storage ottomans or nightstands with drawers.',
      },
      {
        question: 'How to create a bedroom oasis on a $3000 budget?',
        answer:
          'Invest in a quality mattress ($800-1200), paint walls in calming colors ($200), add blackout curtains ($150), choose multifunctional furniture like storage beds ($600), and include soft bedding and decor ($400-600). Focus on comfort and tranquility over trendy decor items.',
      },
      {
        question: 'Best bedroom color schemes for better sleep',
        answer:
          "Choose calming colors like soft blues (Benjamin Moore Quietude), warm grays (Sherwin Williams Accessible Beige), or sage green for walls. Use cool-toned bedding in whites and light grays. Avoid stimulating colors like bright red or orange. Consider the room's natural light - north-facing rooms benefit from warmer tones.",
      },
      {
        question: 'How to design a bedroom for better sleep quality',
        answer:
          'Create a sleep sanctuary by minimizing electronics, using blackout curtains, maintaining consistent room temperature (65-68°F), incorporating soft textures and layers, adding white noise machines or soft acoustic panels, and ensuring proper mattress support. Position the bed away from doors and windows for security and privacy.',
      },
      {
        question: 'Smart bedroom technology integration ideas',
        answer:
          'Install smart lighting with wake-up simulation, smart thermostats for temperature control, automated blackout curtains, smart mirrors with built-in displays, voice-controlled entertainment systems, and smart plugs for charging devices. Consider sleep tracking devices and smart mattresses that monitor sleep quality.',
      },
      {
        question: 'Sustainable bedroom materials for eco-conscious design',
        answer:
          'Choose organic cotton bedding and curtains, bamboo or reclaimed wood furniture, natural fiber rugs (wool or sisal), low-VOC paints, and recycled glass or cork for accent pieces. Look for Energy Star certified electronics and LED lighting. Consider upcycling existing furniture rather than buying new.',
      },
      {
        question: 'Bedroom layout ideas for couples with different needs',
        answer:
          "Create dual bedside zones with adjustable lighting, provide individual storage spaces, include a shared seating area, consider dual-zone climate control, and ensure equal access to windows and doors. Use a king-size bed with room for both partners' comfort items and consider adjustable beds for different sleep preferences.",
      },
      {
        question: 'How to organize a walk-in closet for maximum efficiency',
        answer:
          'Divide the space into zones: hanging clothes, folded items, shoes, and accessories. Use adjustable shelving and rods, install drawer organizers and dividers, add pull-out baskets for accessories, include a full-length mirror with integrated storage, and consider smart lighting with motion sensors.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'bathroom',
    name: 'Bathroom',
    faqs: [
      {
        question: 'What materials work best for bathroom flooring?',
        answer:
          'Water-resistant options like porcelain or ceramic tile, luxury vinyl tile (LVT), natural stone (with proper sealing), or engineered wood are ideal for bathrooms. Consider slip resistance, maintenance requirements, and moisture protection.',
      },
      {
        question: 'How can I make a small bathroom feel larger?',
        answer:
          'Use light colors, install a large mirror, choose a glass shower door instead of a curtain, use the same tile on the floor and walls, select wall-mounted fixtures and vanities, and ensure proper lighting throughout the space.',
      },
      {
        question: 'What should I consider when choosing bathroom fixtures?',
        answer:
          "Consider water efficiency, your home's water pressure, space constraints, design style (traditional, modern, etc.), finish durability, ease of cleaning, and coordinating finishes with other hardware in the room.",
      },
      {
        question: 'How to create a spa-like bathroom on a $3000 budget?',
        answer:
          'Focus on affordable upgrades like subway tile ($400), a frameless glass shower door ($300), plush towels and bath mats ($200), soft LED lighting ($150), and aromatherapy accessories ($100). Skip expensive fixtures and focus on ambiance-creating elements like plants, candles, and natural textures.',
      },
      {
        question: 'Best bathroom layouts for accessibility and aging in place',
        answer:
          'Install zero-step showers with benches, wall-mounted toilets for easier cleaning, grab bars disguised as towel bars, roll-out shelves in cabinets, curbless shower entries, handheld showerheads with multiple settings, and adjustable-height vanities. Ensure 36" wide doorways and clear floor space for wheelchair navigation.',
      },
      {
        question: 'How to integrate smart technology into bathroom design',
        answer:
          'Install smart mirrors with LED lighting and defogging, voice-activated faucets and toilets, smart shower systems with programmable temperatures, automated exhaust fans, smart towel warmers, and waterproof smart plugs for devices. Consider a smart home hub to control all systems centrally.',
      },
      {
        question: 'Sustainable bathroom materials for eco-friendly renovations',
        answer:
          'Choose bamboo or cork flooring, recycled glass countertops, low-flow faucets and toilets, rainwater harvesting systems, natural stone with responsible sourcing, biodegradable cleaning products, and energy-efficient lighting. Look for WaterSense and ENERGY STAR certified products.',
      },
      {
        question: 'Bathroom color schemes for different moods and functions',
        answer:
          'For calming spa vibes: soft blues and greens with white accents. For energizing mornings: bright yellows and oranges with crisp whites. For luxurious elegance: charcoal grays with gold accents. For family bathrooms: cheerful blues with playful patterns. Always use light colors to maximize perceived space.',
      },
      {
        question: 'Best storage solutions for organized bathrooms',
        answer:
          'Install over-toilet shelving for linens, use lazy Susans in cabinets, add drawer organizers for toiletries, incorporate recessed niches in showers, use magnetic strips for metal tools, install wall-mounted cabinets above toilets, and consider under-sink pull-out baskets for cleaning supplies.',
      },
      {
        question: 'How to design a bathroom for multiple users',
        answer:
          'Create dual vanity sinks, install towel hooks at varying heights, provide individual storage cubbies, use shared medicine cabinets with adjustable shelves, add step stools for children, include both a shower and separate tub if space allows, and ensure adequate electrical outlets for personal devices.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'attic',
    name: 'Attic',
    faqs: [
      {
        question: 'What are the best ways to utilize an attic space?',
        answer:
          'Attics can be transformed into bedrooms, home offices, playrooms, reading nooks, or storage spaces. Consider the ceiling height, insulation needs, and natural light sources when planning your attic renovation.',
      },
      {
        question: 'How do I address temperature control in an attic room?',
        answer:
          'Attics can be prone to temperature extremes. Ensure proper insulation in walls and ceiling, install ceiling fans to circulate air, consider mini-split HVAC systems, and use window treatments to control sunlight and heat gain.',
      },
      {
        question: 'What lighting solutions work best for attic spaces?',
        answer:
          'Due to sloped ceilings and limited wall space, recessed lighting, track lighting, and floor lamps are excellent options. Consider adding skylights or dormer windows to maximize natural light, which can make the space feel larger.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'kitchen',
    name: 'Kitchen',
    // Dated oak kitchen (GPT Image 2 Medium) morphing in-frame into a
    // Scandinavian redesign (Seedance 2.0 Mini first->last frame), then
    // crossfading through Farmhouse and Industrial edits of the same photo
    // (Nano Banana 2 image-to-image, layout pinned) and back to the "before"
    // so the loop is seamless. 720x720, 10.6s, muted.
    heroVideo:
      'https://media.easy-peasy.ai/landing/room-types/kitchen-hero.mp4',
    faqs: [
      {
        question: 'What are the most practical kitchen layouts?',
        answer:
          'The most efficient kitchen layouts are the galley, L-shape, U-shape, and island kitchen. Each works best for different space dimensions and needs. The work triangle (sink, stove, refrigerator) should be arranged to minimize steps while cooking.',
      },
      {
        question: 'How do I maximize storage in a small kitchen?',
        answer:
          'Utilize vertical space with tall cabinets, install cabinets up to the ceiling, use pull-out pantry systems, add drawer organizers, install hooks or magnetic strips for utensils, and consider over-the-sink cutting boards or colanders.',
      },
      {
        question: 'What countertop materials offer the best durability?',
        answer:
          'Quartz offers excellent durability and low maintenance without sealing. Granite is very durable but requires periodic sealing. Solid surface (like Corian) is repairable and seamless. Butcher block adds warmth but needs regular maintenance. Porcelain is emerging as a durable, heat and stain-resistant option.',
      },
      {
        question: 'How to renovate a kitchen on a $5000 budget?',
        answer:
          'Focus on painting cabinets ($500-800), replacing hardware ($200-300), updating countertops if needed ($1500-2000), installing new flooring ($800-1200), and adding energy-efficient lighting ($300-500). Save money by DIY painting and refinishing existing cabinets rather than full replacement. Consider open shelving instead of upper cabinets for a modern look.',
      },
      {
        question: 'Best kitchen layouts for small apartments',
        answer:
          'Galley kitchens work best in narrow spaces with appliances on opposite walls. L-shaped layouts maximize corner spaces efficiently. Peninsula layouts add extra counter space and seating without taking floor space. Consider pocket doors to visually open the space and use light colors with plenty of under-cabinet lighting to make the area feel larger.',
      },
      {
        question: 'How to integrate smart appliances into kitchen design',
        answer:
          'Install smart refrigerators with inventory tracking, WiFi-enabled ovens that can be preheated remotely, smart coffee makers with app control, voice-activated lighting and exhaust fans, and smart plugs for small appliances. Choose appliances that work with your smart home ecosystem and consider under-cabinet charging stations for devices.',
      },
      {
        question: 'Sustainable kitchen renovation ideas for eco-friendly homes',
        answer:
          'Choose FSC-certified wood for cabinets, recycled glass or quartz countertops, energy-efficient LED lighting and appliances, low-flow faucets and dual-flush toilets if applicable, and use non-toxic, low-VOC paints and finishes. Consider composting systems, rainwater harvesting for outdoor use, and energy-efficient ventilation systems.',
      },
      {
        question: 'Kitchen island ideas for different space sizes',
        answer:
          'For small kitchens (under 150 sq ft), choose a mobile island on wheels. Medium kitchens benefit from L-shaped islands with storage. Large kitchens can accommodate islands with sinks, cooktops, and seating. Include electrical outlets, lighting, and consider multi-level surfaces for different tasks. Always maintain the work triangle efficiency.',
      },
      {
        question: 'Best cabinet storage solutions for organized kitchens',
        answer:
          'Install pull-out drawers for pots and pans, lazy Susans in corner cabinets, tiered shelf organizers for canned goods, built-in spice racks, and utensil dividers. Use vertical space with pegboards or magnetic strips for knives and tools. Consider appliance garages to hide small appliances and drawer organizers for utensils and baking supplies.',
      },
      {
        question: 'How to choose the right kitchen faucet for your needs',
        answer:
          'Single-handle faucets offer ease of use and water temperature control. Pull-out spray faucets provide flexibility for filling pots. Consider height (8-10 inches for standard use), finish durability (stainless steel or brass), and flow rate (1.5-2.2 gallons per minute for efficiency). Motion-sensor faucets add convenience in busy kitchens.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'kids-room',
    name: 'Kids Room',
    // Before -> after loop built by scratchpad/room_pipeline.py: plain room
    // (GPT Image 2 Medium) morphing into the first style (Seedance 2.0 Mini
    // first->last frame), crossfading through two more layout-pinned edits
    // (Nano Banana 2) and back to the before. 720x720, 10.6s, muted.
    heroVideo:
      'https://media.easy-peasy.ai/landing/room-types/kids-room-hero.mp4',
    faqs: [
      {
        question: 'How can I design a kids room that will grow with my child?',
        answer:
          'Choose furniture that can adapt over time, like convertible cribs or extendable beds. Use easily changeable decor elements like wall art, rugs, and accessories rather than permanent themed elements. Opt for neutral base colors with accent colors that can be updated easily.',
      },
      {
        question: 'What are the best storage solutions for a kids room?',
        answer:
          'Low, open shelving for frequently used toys, labeled bins for easy cleanup, under-bed storage for seasonal items, wall-mounted organizers to maximize floor space, and multi-functional furniture like storage ottomans or beds with drawers are all excellent options.',
      },
      {
        question: 'How do I create a room that balances play and sleep?',
        answer:
          'Consider zoning the room with a clear play area and sleep area. Use rugs to define spaces, ensure adequate lighting for both play (bright) and bedtime (dimmable), and create a designated reading nook or quiet corner for unwinding before sleep.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'dining-room',
    name: 'Dining Room',
    faqs: [
      {
        question: 'What size dining table works best for my space?',
        answer:
          'Allow 36 inches between the table edge and walls or furniture for chair movement and walk space. Each person needs about 24 inches of table width. For rectangular tables, add 12 inches to the length for each head of the table. Round tables work well in smaller spaces and promote conversation.',
      },
      {
        question: 'How should I light my dining room?',
        answer:
          'A chandelier or pendant light centered over the table should hang 30-36 inches above the table surface. The fixture diameter should be about 1/2 to 2/3 the width of your table. Include dimmers for ambiance and consider additional lighting like wall sconces or recessed lights on separate switches.',
      },
      {
        question: "What's the best way to add storage to a dining room?",
        answer:
          'Consider a sideboard or buffet for storing serving pieces and linens. A china cabinet can display special dishware while providing storage. Built-in cabinetry or floating shelves can maximize wall space. For smaller spaces, consider a table with a leaf that can be extended for larger gatherings.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'study-room',
    name: 'Study Room',
    faqs: [
      {
        question: 'How should I position the desk in a study room?',
        answer:
          'Position the desk to take advantage of natural light without creating screen glare. Facing a window can provide inspiration, while the "command position" (desk facing the door but not directly in line with it) offers greater focus and less distraction from movement.',
      },
      {
        question: 'What lighting is best for reducing eye strain?',
        answer:
          'Layer your lighting with ambient room lighting, task lighting (like an adjustable desk lamp), and accent lighting. Position task lighting to illuminate your work without creating shadows or glare. Consider natural daylight bulbs (5000-6500K) for better concentration and reduced eye strain.',
      },
      {
        question: 'How can I create a distraction-free study environment?',
        answer:
          'Choose a quiet location away from high-traffic areas, use noise-canceling headphones or a white noise machine, keep the space organized with adequate storage, face away from doorways or windows if they create distractions, and keep electronics unrelated to study out of the space.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'gaming-room',
    name: 'Gaming Room',
    faqs: [
      {
        question: "What's the ideal setup for a gaming room?",
        answer:
          'Focus on comfortable, ergonomic seating suitable for long sessions, arrange screens to reduce glare and eye strain, include adequate desk or table space for peripherals, ensure proper ventilation for hardware, and organize cables for safety and aesthetics.',
      },
      {
        question: 'How should I approach lighting in a gaming room?',
        answer:
          'Use bias lighting behind monitors to reduce eye strain, install dimmable overhead lighting to prevent screen glare, consider LED strip lighting for ambiance without distraction, and use task lighting when needed for non-gaming activities.',
      },
      {
        question:
          'What acoustic treatments should I consider for a gaming room?',
        answer:
          'Incorporate soft surfaces like rugs, curtains, and upholstered furniture to absorb sound. Consider acoustic panels on walls to reduce echo, use bookcases filled with books as natural sound diffusers, and consider acoustic ceiling tiles or foam ceiling panels in severe cases.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'home-office',
    name: 'Home Office',
    faqs: [
      {
        question: 'What is the ideal desk setup for a productive home office?',
        answer:
          'Position your desk near natural light but avoid screen glare. Ensure you have enough surface area for your equipment, proper cable management, and an ergonomic chair. Consider a sit-stand desk to vary your working position throughout the day.',
      },
      {
        question: 'How can I minimize distractions in my home office?',
        answer:
          'Choose a quiet location, use noise-cancelling headphones, install sound-absorbing materials like rugs and curtains, set boundaries with family members, and consider a door that can be closed for privacy and concentration.',
      },
      {
        question: 'What storage solutions work best for a home office?',
        answer:
          'Combine closed storage (cabinets) for visual clutter with open shelving for frequently accessed items. Use vertical wall space for shelving, consider mobile filing cabinets that can be tucked away, and utilize desk organizers for small items.',
      },
      {
        question: 'How to create a productive home office on a $1000 budget?',
        answer:
          'Invest in an ergonomic chair ($300-400), a sturdy desk ($200-300), proper lighting ($100), cable management solutions ($50), and organizational accessories ($100-200). Focus on comfort and functionality over expensive decor. Shop second-hand for furniture and use DIY solutions for storage.',
      },
      {
        question: 'Best ergonomic home office setup for long work hours',
        answer:
          'Position your monitor at eye level with the top of the screen at or slightly below eye height. Keep your keyboard and mouse at elbow height. Use a footrest if needed. Ensure proper chair height so feet rest flat on floor. Take regular breaks for stretching and maintain proper posture throughout the day.',
      },
      {
        question: 'How to integrate smart home technology into office design',
        answer:
          'Install smart lighting with adjustable brightness and color temperature, smart plugs for equipment control, voice-activated assistants for hands-free operation, smart thermostats for optimal working temperature, automated blinds for natural light control, and smart locks for security when working alone.',
      },
      {
        question:
          'Home office lighting solutions for better focus and productivity',
        answer:
          'Use task lighting (600-800 lumens) directly at your workspace, ambient lighting (300-500 lumens) for general illumination, and accent lighting to highlight work areas. Position lights to avoid glare on screens. Consider daylight-simulating bulbs for morning alertness and warmer tones for evening work.',
      },
      {
        question: 'Sustainable home office materials and practices',
        answer:
          'Choose furniture made from recycled materials or sustainably harvested wood, use energy-efficient LED lighting, opt for bamboo or cork flooring, recycle paper and electronics responsibly, use rechargeable batteries, and choose low-VOC paints and finishes for better indoor air quality.',
      },
      {
        question: 'How to design a home office that converts to a guest room',
        answer:
          'Use a Murphy bed or sofa bed that folds away, choose multi-functional furniture like storage ottomans, install wall-mounted desks that can be folded up, use portable screens or curtains to hide work areas, and ensure the space has adequate ventilation and natural light for both functions.',
      },
      {
        question: 'Best acoustic treatments for noisy home office environments',
        answer:
          'Install acoustic panels on walls (especially behind and beside your work area), use thick rugs or carpets to absorb sound, add heavy curtains or sound-absorbing drapes, place bookshelves filled with books as natural diffusers, use white noise machines, and consider soundproofing the door with weatherstripping.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'meeting-room',
    name: 'Meeting Room',
    faqs: [
      {
        question: "What's the best layout for a productive meeting room?",
        answer:
          'Arrange seating to ensure all participants can see each other, typically in a circle, square, or U-shape. Position the main display or presentation area where everyone has a clear view. Ensure adequate space between chairs for comfort and movement.',
      },
      {
        question: 'How should technology be integrated into a meeting room?',
        answer:
          'Plan for accessible power outlets and USB charging stations, ensure proper cable management to avoid trip hazards, consider built-in videoconferencing equipment, and include adequate lighting for both in-person interaction and video calls.',
      },
      {
        question: 'What elements create an inspiring meeting environment?',
        answer:
          'Include writable surfaces like whiteboards or glass boards for brainstorming, consider natural light and views when possible, incorporate plants for biophilic benefits, ensure good acoustics to minimize echo, and maintain comfortable temperature control.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'podcast-studio',
    name: 'Podcast Studio',
    faqs: [
      {
        question:
          'What acoustic treatments are necessary for a podcast studio?',
        answer:
          'Use acoustic panels on walls and ceiling to absorb sound reflections, incorporate bass traps in corners, add a rug to reduce floor reflections, consider window treatments to block outside noise, and use a sound isolation shield around microphones for cleaner recordings.',
      },
      {
        question: 'How should I set up the recording area in a podcast studio?',
        answer:
          'Position microphones away from walls and hard surfaces, arrange seating to allow guests to face each other naturally, use adjustable microphone arms for flexibility, ensure proper cable management to avoid noise from movement, and consider acoustic dividers between participants if needed.',
      },
      {
        question: 'What lighting works best for a podcast studio?',
        answer:
          "Use soft, diffused lighting that doesn't create harsh shadows or heat. Consider LED panel lights with adjustable brightness, position lights to illuminate faces evenly (especially important for video podcasts), and avoid fluorescent lighting that can cause buzzing noise on recordings.",
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'workshop',
    name: 'Workshop',
    faqs: [
      {
        question: 'How should I organize tools in my workshop?',
        answer:
          'Use shadow boards for frequently used hand tools, install peg boards for visual organization, use labeled drawers and bins for small items, keep power tools in dedicated storage when not in use, and arrange workstations based on workflow to minimize movement between tasks.',
      },
      {
        question: 'What flooring is best for a workshop?',
        answer:
          'Concrete with epoxy coating offers durability and easy cleanup, anti-fatigue mats in standing areas reduce strain, interlocking rubber tiles provide cushioning and are easy to replace in sections, and plywood provides a resilient surface that can be replaced as needed.',
      },
      {
        question: 'How should I address ventilation in a workshop?',
        answer:
          'Install exhaust fans to remove dust and fumes, consider air filtration systems for fine particles, ensure adequate fresh air intake, use task-specific dust collection at the source, and consider portable air cleaners for additional filtration, especially when working with finishes or adhesives.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'gym',
    name: 'Gym',
    // Before -> after loop built by scratchpad/room_pipeline.py: plain room
    // (GPT Image 2 Medium) morphing into the first style (Seedance 2.0 Mini
    // first->last frame), crossfading through two more layout-pinned edits
    // (Nano Banana 2) and back to the before. 720x720, 10.6s, muted.
    heroVideo: 'https://media.easy-peasy.ai/landing/room-types/gym-hero.mp4',
    faqs: [
      {
        question: "What's the best flooring for a home gym?",
        answer:
          'Rubber flooring offers impact absorption and equipment protection, foam interlocking tiles provide cushioning for floor exercises, vinyl flooring is durable and easy to clean, and specialized shock-absorbing flooring is ideal for areas with heavy weights or high-impact activities.',
      },
      {
        question: 'How should I ventilate my home gym?',
        answer:
          'Ensure good air circulation with ceiling fans, consider a dedicated HVAC zone if possible, install windows that can be opened for fresh air, use dehumidifiers in humid climates to control moisture, and maintain a temperature range of 68-72°F (20-22°C) for optimal workouts.',
      },
      {
        question:
          "What's the ideal layout for a home gym based on the available space?",
        answer:
          'Place cardio equipment near windows when possible, ensure adequate clearance around all equipment (especially for moving parts), arrange equipment based on usage frequency, create zones for different activities (cardio, strength, stretching), and include storage for smaller equipment and accessories.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'coffee-shop',
    name: 'Coffee Shop',
    faqs: [
      {
        question: 'What makes an effective coffee shop layout?',
        answer:
          'Create clear customer flow from entrance to ordering to pickup, balance quick-turnover seating with longer-stay areas, ensure adequate space behind counters for staff movement, position the espresso machine to be visible but not create bottlenecks, and include a mix of seating types to accommodate different customer preferences.',
      },
      {
        question: 'How can I create the right ambiance in a coffee shop?',
        answer:
          'Layer lighting with pendant lights over service areas, softer ambient lighting in seating areas, and task lighting where needed. Use a mix of seating heights and types, incorporate natural elements like plants and wood, consider acoustic treatments to manage noise levels, and use color psychology to create the desired mood.',
      },
      {
        question: 'What design elements encourage customers to stay longer?',
        answer:
          'Provide comfortable seating with good back support, offer a mix of individual and group seating arrangements, ensure adequate power outlets for laptops, maintain a comfortable temperature, create partial visual barriers between seating areas for a sense of privacy, and use artwork or distinctive design elements to create a memorable experience.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'clothing-store',
    name: 'Clothing Store',
    faqs: [
      {
        question: 'How do I create an effective clothing store layout?',
        answer:
          'Design clear pathways that guide customers through the store, create focal points with featured merchandise, use the rule of three for visual displays, ensure adequate space between racks for comfortable browsing, and position high-margin items at eye level and checkout areas.',
      },
      {
        question: 'What lighting works best for a clothing store?',
        answer:
          'Use bright, even lighting throughout the store with a minimum of 50 foot-candles, install accent lighting to highlight featured products, ensure fitting rooms have flattering lighting that mimics natural light, and use LED lights with high color rendering index (CRI) to show true fabric colors.',
      },
      {
        question: 'How should I organize merchandise in my clothing store?',
        answer:
          'Group items by category, size, and color for easy browsing, create seasonal displays near the entrance, use the power wall (right side of entrance) for new arrivals, implement cross-merchandising by pairing complementary items, and regularly rotate displays to keep the store fresh.',
      },
      {
        question:
          'What design elements encourage customers to spend more time shopping?',
        answer:
          'Create comfortable seating areas for companions, use mirrors strategically throughout the store, maintain an organized and clutter-free environment, incorporate music and scents that match your brand, and design clear sight lines so customers can see the entire store layout.',
      },
      {
        question:
          'How do I design fitting rooms that enhance the shopping experience?',
        answer:
          'Make fitting rooms spacious enough to move comfortably, install hooks and benches for convenience, use flattering lighting and full-length mirrors, ensure privacy with proper doors or curtains, and consider adding a communal area outside fitting rooms for feedback from friends.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'walk-in-closet',
    name: 'Walk in Closet',
    // Before -> after loop built by scratchpad/room_pipeline.py: plain room
    // (GPT Image 2 Medium) morphing into the first style (Seedance 2.0 Mini
    // first->last frame), crossfading through two more layout-pinned edits
    // (Nano Banana 2) and back to the before. 720x720, 10.6s, muted.
    heroVideo:
      'https://media.easy-peasy.ai/landing/room-types/walk-in-closet-hero.mp4',
    faqs: [
      {
        question: 'How do I maximize storage in a walk-in closet?',
        answer:
          'Use vertical space with double-hanging rods where possible, install adjustable shelving for flexibility, add pull-out drawers and baskets for smaller items, utilize the back of the door for accessories, and consider an island or peninsula for additional storage and folding space if room allows.',
      },
      {
        question: 'What lighting is best for a walk-in closet?',
        answer:
          'Layer lighting with overhead fixtures for general illumination, LED strip lighting in cabinets and shelves, focused lighting near mirrors, and consider natural light where possible. Use bulbs with high CRI (color rendering index) to accurately see clothing colors.',
      },
      {
        question:
          'How should I organize different types of clothing in my closet?',
        answer:
          'Group similar items together (shirts, pants, dresses), organize by color within each category, store seasonal items at higher or lower levels based on frequency of use, use specialized hangers for different garment types, and keep frequently used items at eye level for easy access.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'toilet',
    name: 'Toilet',
    faqs: [
      {
        question: 'What are the best space-saving solutions for small toilets?',
        answer:
          'Maximize vertical space with wall-mounted toilets and floating vanities, use corner sinks to free up floor space, install recessed shelving in walls for storage, choose sliding or pocket doors instead of traditional swing doors, and incorporate multi-functional fixtures like toilet-top sinks that reuse handwashing water for flushing.',
      },
      {
        question: 'How can I improve ventilation in a windowless toilet?',
        answer:
          'Install a powerful exhaust fan rated for your room size (CFM rating should be at least 1 CFM per square foot), ensure the fan vents to the outside not just the attic, consider a humidity-sensing fan that activates automatically, add an air purifier for additional freshness, and keep the door slightly open when not in use to promote air circulation.',
      },
      {
        question:
          'What materials are most hygienic and easy to maintain in toilets?',
        answer:
          'Choose non-porous surfaces like porcelain, ceramic, or quartz that resist bacteria growth, use epoxy grout instead of cement-based for better water resistance, select wall-mounted fixtures to eliminate hard-to-clean floor connections, install antimicrobial coatings on high-touch surfaces, and opt for smooth, seamless surfaces that prevent dirt accumulation.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'restaurant',
    name: 'Restaurant',
    // Before -> after loop built by scratchpad/room_pipeline.py: plain room
    // (GPT Image 2 Medium) morphing into the first style (Seedance 2.0 Mini
    // first->last frame), crossfading through two more layout-pinned edits
    // (Nano Banana 2) and back to the before. 720x720, 10.6s, muted.
    heroVideo:
      'https://media.easy-peasy.ai/landing/room-types/restaurant-hero.mp4',
    faqs: [
      {
        question:
          'How do I optimize restaurant layout for efficient service flow?',
        answer:
          'Design clear pathways between kitchen and dining areas with minimum 36-inch aisles for staff movement, position service stations strategically to minimize travel distance, create distinct zones for waiting, dining, and service preparation, ensure the kitchen pass is visible from most server positions, and separate customer and staff traffic flows to prevent congestion.',
      },
      {
        question:
          'What seating arrangements maximize restaurant capacity while maintaining comfort?',
        answer:
          'Mix table sizes to accommodate different party sizes (30% two-tops, 40% four-tops, 30% larger tables), maintain 24-30 inches between tables for comfortable movement, use banquette seating along walls to maximize floor space, incorporate flexible furniture that can be reconfigured for groups, and consider bar and counter seating for solo diners and quick service.',
      },
      {
        question:
          'How can restaurant lighting create the right dining atmosphere?',
        answer:
          'Layer lighting with dimmable ambient lighting for overall illumination, pendant lights over each table for intimate dining (30-36 inches above table surface), accent lighting to highlight design features or artwork, bright task lighting in service areas for safety, and separate lighting zones for different times of day or special events.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'bar',
    name: 'Bar',
    faqs: [
      {
        question:
          'What is the ideal bar counter height and depth for functionality?',
        answer:
          'Standard bar height is 42 inches with bar stools at 30 inches, counter depth should be 16-20 inches for patron comfort with 24-30 inches on the bartender side for workspace, include a footrest 7-9 inches from the floor, ensure 24-30 inches of width per seat, and add an overhang of 10-12 inches for knee room.',
      },
      {
        question: 'How should I design bar storage for maximum efficiency?',
        answer:
          'Organize bottles by frequency of use with premium spirits at eye level, install speed rails for most-used liquors within easy reach, use tiered shelving for visibility and aesthetic appeal, incorporate refrigeration under the bar for beer and mixers, include dry storage for garnishes and supplies, and position glassware based on drink preparation workflow.',
      },
      {
        question: 'What lighting design works best for bars?',
        answer:
          'Install dimmable ambient lighting for atmosphere control throughout the evening, use LED strip lighting under the bar for subtle accent and safety, backlight bottle displays to create visual interest and showcase inventory, ensure adequate task lighting for bartenders without creating glare, and consider color-changing options for different moods or events.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'office',
    name: 'Office',
    faqs: [
      {
        question:
          'What is the optimal office layout for productivity and collaboration?',
        answer:
          "Create a mix of open collaborative spaces and private focus areas, position workstations to minimize distractions while maintaining team connectivity, include breakout spaces for informal meetings, ensure natural light reaches as many workstations as possible, provide adequate space per person (minimum 60-80 square feet), and design clear circulation paths that don't disrupt work areas.",
      },
      {
        question:
          'How can I design an office that supports employee wellbeing?',
        answer:
          'Maximize natural light and provide views to the outside where possible, incorporate biophilic design elements like plants and natural materials, ensure proper ventilation and air quality control, provide sit-stand desk options for ergonomic flexibility, create quiet zones for focused work and relaxation, and include amenities like wellness rooms or comfortable break areas.',
      },
      {
        question:
          'What acoustic solutions work best for open office environments?',
        answer:
          'Install acoustic ceiling tiles or clouds to absorb sound from above, use sound-absorbing panels on walls strategically placed at ear level, incorporate soft furnishings and carpeting to reduce noise reflection, create phone booths or quiet rooms for private conversations, position noisy equipment away from workstations, and consider white noise systems to mask distracting sounds.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'coworking-space',
    name: 'Coworking Space',
    faqs: [
      {
        question: 'How do I create flexible zones in a coworking space?',
        answer:
          'Design distinct areas for different work styles: hot desks for flexibility, dedicated desks for regulars, private offices for teams, phone booths for calls, lounge areas for informal work, and meeting rooms of various sizes. Use modular furniture that can be reconfigured, clear signage for zone identification, and different flooring or color schemes to define spaces.',
      },
      {
        question:
          'What amenities are essential for a successful coworking space?',
        answer:
          'Provide high-speed WiFi with redundant connections, abundant power outlets and USB charging stations, secure storage lockers for personal items, printing and scanning facilities, kitchen with coffee/tea stations, phone booths for private calls, variety of meeting rooms with booking systems, and comfortable break areas for networking.',
      },
      {
        question:
          'How can I balance privacy and community in coworking design?',
        answer:
          'Create a gradient from social to private spaces, use semi-transparent partitions for visual privacy without isolation, design communal areas that encourage interaction, provide noise zones from quiet to collaborative, include bookable private spaces for focused work, and arrange furniture to allow users to choose their level of engagement.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'hotel-lobby',
    name: 'Hotel Lobby',
    faqs: [
      {
        question:
          'What design elements create a welcoming hotel lobby atmosphere?',
        answer:
          'Create a clear focal point upon entry such as artwork or a feature wall, ensure the reception desk is immediately visible and accessible, use warm lighting with layers for different times of day, incorporate comfortable seating areas for waiting and socializing, add local art or cultural elements for sense of place, and maintain clear sightlines for security and navigation.',
      },
      {
        question: 'How should I plan seating arrangements in a hotel lobby?',
        answer:
          'Provide varied seating options: intimate groupings for private conversations, communal tables for co-working, comfortable lounge chairs for waiting, bar-height seating for quick meetings, and quiet corners for reading. Ensure 30-40% of lobby space is dedicated to seating, position furniture to create natural traffic flows, and include power outlets at every seating area.',
      },
      {
        question:
          'What flooring materials work best for high-traffic hotel lobbies?',
        answer:
          'Choose durable materials like porcelain tile, natural stone, or commercial-grade luxury vinyl that can withstand heavy foot traffic and luggage wheels. Consider slip resistance ratings for safety, use entrance mats to protect flooring and reduce cleaning, incorporate acoustic underlayment to minimize noise, and select patterns that hide wear and dirt between cleanings.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'hotel-room',
    name: 'Hotel Room',
    faqs: [
      {
        question: 'What layout maximizes space efficiency in hotel rooms?',
        answer:
          'Position the bed as the focal point with clear access from both sides, create a defined entry area with closet and luggage storage, place the work desk near natural light with accessible power outlets, design the bathroom with sliding doors to save space, use wall-mounted fixtures and furniture where possible, and incorporate multi-functional pieces like ottoman storage or sofa beds.',
      },
      {
        question:
          'How can hotel room design enhance guest comfort and sleep quality?',
        answer:
          'Install blackout curtains with secondary sheer curtains for light control, use sound-insulating materials in walls and floors, provide individual climate control with quiet HVAC systems, choose quality mattresses with hypoallergenic bedding options, include bedside controls for lighting and temperature, and position electrical outlets conveniently for device charging.',
      },
      {
        question:
          'What technology integration improves the hotel room experience?',
        answer:
          'Incorporate smart room controls for lighting, temperature, and entertainment, provide ample USB and power outlets at desk and bedside, install smart TVs with streaming capabilities, offer keyless entry systems for security and convenience, include wireless charging stations on nightstands, and ensure strong WiFi coverage throughout the room.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'hotel-bathroom',
    name: 'Hotel Bathroom',
    faqs: [
      {
        question: 'What features make a hotel bathroom feel luxurious?',
        answer:
          'Install rainfall showerheads with handheld options for flexibility, use large-format tiles or natural stone for premium feel, provide double vanities in larger rooms for convenience, include heated floors and towel warmers for comfort, incorporate indirect lighting around mirrors for flattering illumination, and add spa-like amenities such as bathrobes and premium toiletries.',
      },
      {
        question: 'How can I design a hotel bathroom for easy maintenance?',
        answer:
          'Choose non-porous surfaces like quartz or solid surface for vanities, use large-format tiles with minimal grout lines, install wall-mounted toilets for easier floor cleaning, select single-handle faucets for simplicity, incorporate adequate ventilation to prevent mold and mildew, and use commercial-grade fixtures designed for heavy use.',
      },
      {
        question: 'What safety features are essential in hotel bathrooms?',
        answer:
          'Install grab bars that blend with the design aesthetic, use slip-resistant flooring and shower surfaces, ensure proper lighting including night lights, maintain consistent water temperature with anti-scald valves, provide GFCI outlets away from water sources, and include emergency call systems in accessible rooms.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'exhibition-space',
    name: 'Exhibition Space',
    faqs: [
      {
        question: 'How should I design flexible exhibition layouts?',
        answer:
          "Use modular wall systems that can be reconfigured for different exhibitions, install ceiling-mounted track systems for flexible lighting and hanging, create clear circulation paths that can adapt to various layouts, provide multiple power and data points throughout the space, incorporate movable display cases and pedestals, and design neutral backgrounds that won't compete with displayed items.",
      },
      {
        question: 'What lighting is best for displaying art and exhibitions?',
        answer:
          'Install adjustable track lighting with multiple circuit controls, use LED lights with high CRI (95+) for accurate color rendering, maintain 200-300 lux for general areas and 300-500 lux for displays, incorporate UV filters to protect sensitive materials, provide separate controls for ambient and accent lighting, and consider natural light with UV-filtering skylights or windows.',
      },
      {
        question: 'How can I control climate for artwork preservation?',
        answer:
          'Maintain stable temperature (68-72°F) and humidity (45-55% RH) levels, install HVAC systems with precise control and monitoring, use vapor barriers in walls to prevent moisture infiltration, incorporate air filtration to remove pollutants, provide separate climate zones for different material requirements, and include data logging systems for documentation.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'onsen',
    name: 'Onsen',
    faqs: [
      {
        question: 'What materials are best for authentic onsen design?',
        answer:
          'Use natural materials like hinoki cypress wood for its antibacterial properties and pleasant aroma, incorporate natural stone such as granite or volcanic rock for durability and heat retention, choose slip-resistant tiles for safety in wet areas, install cedar or bamboo for ceiling and wall accents, and select materials that can withstand high humidity and temperature fluctuations.',
      },
      {
        question: 'How do I create proper ventilation in an onsen space?',
        answer:
          'Design natural ventilation with high windows or vents for steam escape, install exhaust fans rated for high humidity environments, incorporate air circulation systems to prevent condensation, maintain negative pressure to prevent moisture spread, use moisture-resistant materials for all ventilation components, and consider traditional Japanese ranma (transom) designs for aesthetic ventilation.',
      },
      {
        question: 'What are the essential zones in onsen design?',
        answer:
          'Create distinct areas: reception for check-in and orientation, changing rooms with lockers and vanity areas, washing stations with seated shower areas, multiple temperature pools (hot, warm, cold), relaxation areas with traditional tatami or modern lounging, and outdoor bathing areas (rotenburo) when possible. Ensure clear separation between genders and provide barrier-free options.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'mudroom',
    name: 'Mudroom',
    faqs: [
      {
        question: 'What storage solutions work best for mudroom organization?',
        answer:
          'Install individual cubbies or lockers for each family member, include bench seating with hidden storage for seasonal items, add hooks at varying heights for coats and bags, incorporate shoe storage with ventilation for wet footwear, use overhead storage for infrequently used items, and include a designated area for sports equipment and outdoor gear.',
      },
      {
        question: 'What flooring materials are most practical for mudrooms?',
        answer:
          'Choose water-resistant and durable options like porcelain or ceramic tile with slip-resistant finish, luxury vinyl tile for comfort and easy maintenance, sealed concrete for industrial durability, or rubber flooring for cushioning and water resistance. Include drainage mats near the entry and ensure flooring can handle snow, mud, and water without damage.',
      },
      {
        question: 'How can I maximize functionality in a small mudroom?',
        answer:
          'Use vertical storage with floor-to-ceiling built-ins, install wall-mounted folding benches to save floor space, incorporate sliding doors instead of swing doors, use corner spaces with custom storage solutions, add mirrors to create illusion of space, and choose light colors to brighten the area. Consider seasonal rotation of stored items.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'drop-zone',
    name: 'Drop Zone',
    faqs: [
      {
        question: 'What are the essential elements of an effective drop zone?',
        answer:
          'Include a surface for keys, mail, and daily items like a console table or wall-mounted shelf, provide hooks or pegs for bags, coats, and accessories, incorporate a charging station for devices with cable management, add a small basket or tray for loose items, include a mirror for last-minute checks, and position near the main entry for convenience.',
      },
      {
        question: 'How can I create a drop zone in a home without an entryway?',
        answer:
          "Utilize wall space near the door with floating shelves and hooks, convert a closet near the entrance into a mini mudroom, use a narrow console table that doesn't impede traffic flow, install a wall-mounted organizer system, create a corner drop zone with a small bench and storage, or incorporate furniture pieces that serve double duty.",
      },
      {
        question: 'What organizational systems keep drop zones clutter-free?',
        answer:
          "Assign specific spots for each family member's belongings, use labeled bins or baskets for categorization, implement a daily reset routine to maintain organization, include a mail sorting system to prevent paper buildup, rotate seasonal items regularly, and keep only essential everyday items in the drop zone.",
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'laundry-room',
    name: 'Laundry Room',
    faqs: [
      {
        question: "What's the ideal layout for a functional laundry room?",
        answer:
          'Create a logical workflow with dirty clothes hampers, washing machine, drying area, folding space, and storage for supplies. Include a utility sink if space allows, counter space for folding, and adequate storage above machines for detergents and supplies.',
      },
      {
        question: 'How can I make my laundry room more efficient?',
        answer:
          'Install floating shelves or cabinets above appliances, use a rolling cart between machines for small spaces, add a wall-mounted drying rack or retractable clothesline, include a pull-out ironing board, and use labeled bins for sorting clothes.',
      },
      {
        question: 'What flooring is best for a laundry room?',
        answer:
          'Ceramic or porcelain tile offers excellent water resistance and durability. Luxury vinyl tile (LVT) provides good moisture resistance with more cushioning. Sealed concrete is durable and affordable. Ensure whatever flooring you choose is slip-resistant and can withstand moisture and cleaning products.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'house-exterior',
    name: 'House Exterior',
    faqs: [
      {
        question:
          'What exterior materials offer the best durability and curb appeal?',
        answer:
          'Fiber cement siding provides excellent durability and fire resistance with low maintenance, natural stone or brick offers timeless appeal and longevity, composite materials combine aesthetics with weather resistance, metal siding provides modern looks with minimal upkeep, and wood requires more maintenance but offers warm, natural beauty. Consider climate, maintenance requirements, and architectural style when choosing.',
      },
      {
        question: "How can I improve my home's curb appeal on a budget?",
        answer:
          'Paint the front door and shutters in a bold, complementary color, update house numbers and mailbox for a modern look, add landscape lighting to highlight architectural features, maintain a well-manicured lawn and add colorful plantings, power wash siding, walkways, and driveway, and install window boxes or container gardens for instant charm.',
      },
      {
        question: 'What color schemes work best for house exteriors?',
        answer:
          "Choose colors that complement the architectural style and neighborhood aesthetic, use the 60-30-10 rule (60% dominant color, 30% secondary, 10% accent), consider the roof color when selecting siding colors, test colors in different lighting conditions throughout the day, factor in fixed elements like stone or brick that can't be changed, and remember that lighter colors make homes appear larger while darker colors create a cozy feel.",
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'outdoor-pool-area',
    name: 'Outdoor Pool Area',
    faqs: [
      {
        question: 'What design elements create the perfect poolside ambiance?',
        answer:
          'Focus on creating zones for different activities: lounging areas with comfortable seating, dining spaces near the pool, a wet bar or refreshment area, and transition spaces with changing facilities. Use natural materials like stone, wood, and water features to create a resort-like atmosphere while maintaining functionality.',
      },
      {
        question: 'How can I ensure safety in my outdoor pool design?',
        answer:
          'Incorporate safety features like proper fencing that meets local codes, non-slip surfaces around the pool deck, clear depth markers and safety signage, adequate lighting for evening visibility, safety equipment stations, and consider natural barriers like landscaping to prevent accidental access.',
      },
      {
        question:
          'What materials work best for pool decking and surrounding areas?',
        answer:
          "Choose water-resistant materials like porcelain or ceramic tile with slip-resistant finishes, natural stone (travertine, limestone, or slate) that develops a natural patina, composite decking that won't warp or splinter, or concrete with proper sealing. All materials should be designed to handle constant moisture and pool chemicals.",
      },
      {
        question: 'How should I arrange outdoor furniture around a pool?',
        answer:
          'Create conversation groupings with seating facing each other, position loungers to take advantage of sun exposure, place dining sets in shaded areas, ensure adequate spacing between furniture for easy movement, and consider multi-level seating to create visual interest and accommodate different group sizes.',
      },
      {
        question: 'What lighting solutions work best for evening pool use?',
        answer:
          'Layer lighting with underwater pool lights for safety and ambiance, pathway lighting to guide guests safely, uplighting on trees or architectural features, string lights or bistro lights for overhead illumination, and perimeter lighting to define the pool area boundaries and enhance security.',
      },
      {
        question: 'How can I create privacy around my pool area?',
        answer:
          'Install tall fencing or privacy walls with attractive finishes, plant screening shrubs or bamboo along property lines, use lattice panels with climbing vines, incorporate trellises or pergolas with privacy fabric panels, position outdoor rooms or cabanas strategically, and consider mature trees for natural screening.',
      },
      {
        question:
          'What maintenance considerations are important for pool areas?',
        answer:
          'Plan for easy access to pool equipment and chemical storage, include durable surfaces that resist stains and fading, incorporate drainage systems to manage water runoff, choose low-maintenance plants that thrive in your climate, include storage solutions for pool toys and equipment, and consider weather protection for furniture during off-seasons.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'outdoor-patio',
    name: 'Outdoor Patio',
    faqs: [
      {
        question: 'What furniture materials work best for outdoor patios?',
        answer:
          'Weather-resistant options include: powder-coated aluminum (lightweight, rust-resistant), teak or cedar (natural weather resistance), all-weather wicker (synthetic, UV-resistant), marine-grade polymer (impervious to moisture), and concrete or stone (extremely durable but permanent).',
      },
      {
        question: 'How can I create different zones on my patio?',
        answer:
          'Use outdoor rugs to define seating and dining areas, position furniture in conversational groupings, add planters as natural dividers, incorporate different levels with deck platforms or steps, and create shade structures over specific areas to differentiate spaces.',
      },
      {
        question: 'What lighting options work best for outdoor patios?',
        answer:
          'Layer with overhead string lights for ambiance, pathway lights for safety, uplighting for architectural features, downlighting from trees or structures, weatherproof table lamps for intimate settings, and consider solar options to avoid running electrical wiring.',
      },
      {
        question: 'How to create a cozy outdoor patio for year-round use?',
        answer:
          'Install outdoor heaters or fire pits for warmth, add weather-resistant cushions and throws, create windbreaks with screens or plants, use durable rugs that can withstand moisture, include covered areas with retractable awnings, and choose furniture that can be easily stored indoors during extreme weather.',
      },
      {
        question: 'Best patio flooring options for different climates',
        answer:
          'For wet climates: composite decking or porcelain pavers that drain well. For dry climates: natural stone or concrete for heat retention. For cold climates: heated pavers or interlocking tiles that can be removed for winter. For coastal areas: pressure-treated wood or composites resistant to salt air.',
      },
      {
        question:
          'Seasonal patio decor ideas for spring, summer, fall, and winter',
        answer:
          'Spring: Bright floral arrangements and pastel cushions. Summer: Tropical plants, vibrant colors, and cooling misters. Fall: Warm oranges and reds with harvest decor. Winter: Evergreen garlands, fairy lights, and neutral textiles. Rotate decor seasonally to maintain fresh appeal year-round.',
      },
      {
        question: 'How to make outdoor patios pet-friendly',
        answer:
          'Choose durable, easy-to-clean surfaces like composite decking, provide shaded areas for pets, install pet doors if applicable, add outdoor water bowls and feeding stations, use pet-safe plants and avoid toxic varieties, include comfortable pet beds, and consider fencing or barriers for safety.',
      },
      {
        question: 'Sustainable outdoor patio materials and practices',
        answer:
          'Use reclaimed wood or bamboo for decking, choose FSC-certified outdoor furniture, install native plants for natural landscaping, use solar-powered lighting, collect rainwater for irrigation, choose low-maintenance drought-resistant plants, and use eco-friendly sealants and cleaners.',
      },
      {
        question: 'Outdoor patio BBQ and entertaining setup ideas',
        answer:
          'Position the grill away from seating areas for safety, create a serving station with countertop space, include a beverage cooler nearby, add outdoor sinks for easy cleanup, provide ample seating with flexible arrangements, and consider built-in storage for grilling tools and accessories.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'outdoor-garden',
    name: 'Outdoor Garden',
    faqs: [
      {
        question: 'How can I design a low-maintenance garden?',
        answer:
          'Choose native plants adapted to your climate, group plants with similar water needs together, use mulch to reduce weeding and retain moisture, install drip irrigation on timers, incorporate hardscaping like paths and patios, and select perennials that return year after year.',
      },
      {
        question: 'What elements should I include in a family-friendly garden?',
        answer:
          'Create open lawn areas for play, include child-safe plants (avoiding toxic varieties), add discovery paths and stepping stones, incorporate sensory elements like water features, provide shaded areas for comfort, and consider a dedicated vegetable garden area for learning.',
      },
      {
        question: 'How can I attract wildlife to my garden?',
        answer:
          'Plant native flowering species that provide nectar for pollinators, include berry-producing shrubs for birds, add a water source like a bird bath or small pond, create habitat areas with brush piles or native grasses, limit chemical use, and incorporate varied plant heights for different wildlife needs.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'outdoor-garage',
    name: 'Outdoor Garage',
    faqs: [
      {
        question:
          'How can I design a detached garage that complements my home?',
        answer:
          'Match architectural elements like rooflines, windows, and trim details to your main house, use the same or complementary siding materials and colors, align the garage placement with existing structures and landscape features, incorporate similar design elements like shutters or decorative hardware, add windows for natural light and visual appeal, and consider a breezeway or covered walkway to connect to the main house.',
      },
      {
        question: 'What features add functionality to an outdoor garage?',
        answer:
          'Include workshop space with built-in workbenches and tool storage, add a second-story loft for storage or living space, install adequate electrical service for power tools and EV charging, incorporate man doors for convenient access without opening main doors, provide good ventilation for fumes and temperature control, and consider plumbing for a utility sink or bathroom.',
      },
      {
        question: 'How do I maximize storage in a detached garage?',
        answer:
          'Install overhead storage systems for seasonal items, use wall-mounted slatwall or pegboard systems for tools, add built-in shelving units along walls, incorporate attic trusses for additional storage above, use the space between studs for shallow storage, and consider a car lift system to double parking capacity in the same footprint.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'garage',
    name: 'Garage',
    faqs: [
      {
        question: 'What are the best storage solutions for a garage?',
        answer:
          'Use wall-mounted systems like track-based panels or slatwall for flexibility, install overhead storage racks for seasonal items, incorporate cabinets for items needing protection from dust, use labeled bins for small parts and items, and consider a workbench with built-in storage for tools.',
      },
      {
        question: "What's the best flooring for a garage?",
        answer:
          'Epoxy coating provides durability and easy cleaning, interlocking garage floor tiles offer customization and are easy to replace in sections, polyaspartic coatings are extremely durable and fast-curing, and concrete sealers provide basic protection at a lower cost.',
      },
      {
        question: 'How can I improve the organization of my garage?',
        answer:
          'Zone your garage by activity (automotive, gardening, sports), use vertical storage to maximize floor space, keep frequently used items at eye level, store seasonal items overhead, use clear bins with labels for visibility, and maintain a regular decluttering schedule.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'garage-gym',
    name: 'Garage Gym',
    faqs: [
      {
        question: 'How do I convert my garage into a functional gym space?',
        answer:
          'Install rubber flooring or interlocking foam mats over concrete for joint protection, ensure adequate ventilation with fans or open garage doors during workouts, add mirrors on walls to check form and create spacious feel, install proper lighting including natural light if possible, consider climate control options for year-round comfort, and plan equipment layout to allow car parking if needed.',
      },
      {
        question: 'What equipment storage solutions work best in garage gyms?',
        answer:
          'Mount weight racks and plate storage on walls to save floor space, use ceiling-mounted pulley systems for cable exercises, install wall hooks for resistance bands and jump ropes, incorporate mobile storage carts for smaller equipment, use vertical barbell storage to prevent warping, and consider foldable equipment that can be stored against walls.',
      },
      {
        question: 'How can I control temperature in my garage gym?',
        answer:
          'Insulate garage doors and walls to maintain stable temperatures, install ceiling fans for air circulation during workouts, use portable heaters for winter and fans or portable AC for summer, consider a mini-split HVAC system for year-round climate control, add weather stripping around doors to prevent drafts, and time workouts during optimal temperature periods when possible.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'carport',
    name: 'Carport',
    faqs: [
      {
        question: 'What roofing materials work best for carports?',
        answer:
          'Metal roofing offers excellent durability and snow shedding capabilities, polycarbonate panels provide UV protection while allowing natural light, asphalt shingles match existing home roofing for cohesive look, fabric canopies offer affordable temporary solutions, and solar panels can provide shade while generating electricity. Consider local climate, budget, and aesthetic preferences.',
      },
      {
        question: 'How can I add storage to my carport without cluttering?',
        answer:
          "Install overhead storage nets or platforms for lightweight items, use the back wall for pegboard or slatwall systems, add lockable storage cabinets for valuable tools, incorporate benches with built-in storage along sides, hang bikes and ladders from ceiling hooks, and use corner spaces for tall storage units that don't impede vehicle access.",
      },
      {
        question: 'What design features enhance carport functionality?',
        answer:
          'Include adequate lighting for evening use and security, install gutters and downspouts for water management, add electrical outlets for tool use or EV charging, consider partial wall enclosures for weather protection, incorporate a workbench area for minor repairs, and ensure sufficient height clearance for larger vehicles or roof cargo.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'basement',
    name: 'Basement',
    faqs: [
      {
        question: 'How can I prevent moisture issues in my basement?',
        answer:
          'Address exterior drainage first (gutters, downspouts, grading), seal foundation cracks, install a sump pump in flood-prone areas, use dehumidifiers to control humidity levels, choose moisture-resistant materials for finishes, and consider a vapor barrier system before finishing walls.',
      },
      {
        question: "What's the best flooring for a basement?",
        answer:
          'Luxury vinyl plank/tile offers excellent moisture resistance and warmth, ceramic tile is highly durable and moisture-resistant, engineered wood can work with proper subfloor preparation, and epoxy coating provides a durable, seamless surface for unfinished spaces.',
      },
      {
        question: 'How can I maximize ceiling height in a basement?',
        answer:
          'Choose recessed lighting instead of hanging fixtures, opt for exposed ceiling treatments rather than dropped ceilings where possible, route ductwork and plumbing along walls or joist bays, use low-profile furniture, and paint ceilings a light color to create the illusion of height.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'wine-cellar',
    name: 'Wine Cellar',
    faqs: [
      {
        question: 'What are the ideal conditions for wine storage?',
        answer:
          'Maintain consistent temperature between 55-58°F (13-14°C) for optimal aging, keep humidity levels at 60-70% to preserve cork integrity, ensure complete darkness or use UV-filtered lighting, minimize vibrations that can disturb sediment, provide adequate ventilation to prevent musty odors, and position bottles horizontally to keep corks moist. Install monitoring systems for tracking conditions.',
      },
      {
        question:
          'What racking systems work best for different wine collections?',
        answer:
          'Use traditional wood racking (cedar or redwood) for classic aesthetics and flexibility, metal systems for modern looks and durability, modular units that can grow with your collection, diamond bins for bulk storage, display rows for showcasing special bottles, and combination systems with different bottle sizes. Include space for cases and large format bottles.',
      },
      {
        question: 'How do I properly insulate and seal a wine cellar?',
        answer:
          "Install vapor barriers on the warm side of walls to prevent condensation, use closed-cell foam insulation for superior R-value and moisture resistance, seal all penetrations including electrical and plumbing, install an exterior-grade door with weather stripping, insulate the ceiling if there's living space above, and consider hiring a specialist for proper climate control system design.",
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'pantry',
    name: 'Pantry',
    faqs: [
      {
        question: 'How should I organize my pantry for maximum efficiency?',
        answer:
          'Group items by category (baking, snacks, canned goods, etc.), use clear containers for bulk items with labels showing contents and dates, place frequently used items at eye level, implement FIFO (first in, first out) rotation for perishables, dedicate zones for different meal types or family members, and keep a running inventory list to prevent overbuying.',
      },
      {
        question:
          'What storage solutions work best for different pantry sizes?',
        answer:
          'For walk-in pantries: use adjustable shelving, add corner lazy Susans, install door racks for spices. For reach-in pantries: maximize vertical space with shelf risers, use pull-out drawers for deep shelves, add over-door organizers. For cabinet pantries: use turntables for corners, stackable bins for categories, and drawer organizers for small items.',
      },
      {
        question: 'How can I maintain proper food storage conditions?',
        answer:
          'Keep temperature between 50-70°F for optimal shelf life, ensure good ventilation to prevent moisture buildup, store items away from direct sunlight, use airtight containers to prevent pest infestations, separate strong-smelling items to prevent flavor transfer, and monitor humidity levels (less than 15% for dry goods). Consider adding a small dehumidifier in humid climates.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'nursery',
    name: 'Nursery',
    faqs: [
      {
        question: 'What are the essential elements of a functional nursery?',
        answer:
          'Include a comfortable crib with firm mattress, a supportive chair for feeding, a changing station with nearby storage for supplies, blackout curtains for better sleep, gentle adjustable lighting, storage for clothes and toys, and a safe baby monitor system.',
      },
      {
        question: 'How should I approach color and decor in a nursery?',
        answer:
          'Consider using calming colors like soft blues, greens, or neutrals as a base, add visual interest with contrasting accents that stimulate development, incorporate washable wall finishes, use removable decor elements that can be updated as the child grows, and include soft textiles for comfort.',
      },
      {
        question:
          'What safety features should I prioritize in a nursery design?',
        answer:
          'Secure all furniture to walls to prevent tipping, use cordless window treatments, ensure proper spacing between crib slats, keep furniture away from windows, choose non-toxic paints and materials, cover electrical outlets, and pad sharp corners on furniture.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'home-theater',
    name: 'Home Theater',
    faqs: [
      {
        question: 'What are the ideal room dimensions for a home theater?',
        answer:
          'Rectangular rooms work best with a ratio of approximately 1:1.6:2.6 (height:width:length). Avoid perfect squares or cubes that create standing sound waves. The ceiling height should ideally be at least 9 feet for proper sound reflection and projector placement.',
      },
      {
        question: 'How should I approach acoustics in my home theater?',
        answer:
          'Apply acoustic panels to treat first reflection points on side walls, add bass traps in corners, include a mix of sound-absorbing and diffusing materials, consider acoustic ceiling treatments, use heavy curtains over windows, and include a thick carpet or rug on the floor.',
      },
      {
        question: "What's the optimal seating arrangement for viewing comfort?",
        answer:
          'Position the main seating area at a distance of 1.5-2.5 times the screen width, arrange seating in a curved configuration for equal viewing angles, elevate rear seating rows by 6-12 inches if possible, and leave adequate space between rows (at least 4 feet) for comfortable movement.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'home-library',
    name: 'Home Library',
    faqs: [
      {
        question: 'What type of shelving works best for a home library?',
        answer:
          'Adjustable shelving allows flexibility for different book sizes, solid wood shelves resist sagging better than particleboard, consider depth carefully (10-12 inches for most books), include some display areas for special volumes, and ensure shelves are properly anchored to walls for safety.',
      },
      {
        question: 'How should I arrange books in my library?',
        answer:
          'Consider organizing by genre/subject for intuitive browsing, alphabetize within categories, place frequently accessed books at eye level, use bookends for partially-filled shelves, display special editions or art books face-out, and leave some open space for new acquisitions.',
      },
      {
        question: 'What lighting is best for a home library?',
        answer:
          'Layer lighting with ambient overhead lights, task lighting near reading areas, accent lighting to highlight collections or architectural features, adjustable-arm reading lamps near seating, and consider LED shelf lighting to illuminate book spines without generating heat.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'sunroom',
    name: 'Sunroom',
    faqs: [
      {
        question: 'What furniture and materials work best in a sunroom?',
        answer:
          'Choose UV-resistant and moisture-resistant furniture like synthetic wicker, aluminum, or treated wood. Select indoor-outdoor fabrics that resist fading and mildew. Consider lightweight, movable pieces to follow the sun, and incorporate materials that can withstand temperature fluctuations.',
      },
      {
        question:
          'How can I control temperature in a sunroom throughout the year?',
        answer:
          'Install double or triple-pane windows with low-E coating, add ceiling fans for air circulation, include window treatments like cellular shades or UV-filtering blinds, consider radiant floor heating for winter months, and add portable heating or cooling units as needed seasonally.',
      },
      {
        question: 'What plants thrive in a sunroom environment?',
        answer:
          "Choose plants based on the room's exposure and temperature range. For sunny rooms, consider citrus trees, succulents, or flowering plants like hibiscus. For partial sun, try ferns, peace lilies, or philodendrons. Group plants with similar light and water needs, and use rolling plant stands for flexibility.",
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'entryway',
    name: 'Entryway',
    faqs: [
      {
        question:
          'How can I make a small entryway feel larger and more welcoming?',
        answer:
          'Use light colors on walls and ceilings to create an open feel, install a large mirror to reflect light and create depth, choose furniture with legs to maintain visual flow, keep decor minimal and functional, use vertical storage to maximize floor space, and ensure adequate lighting with both ambient and accent fixtures. Add a pop of color with artwork or an accent wall.',
      },
      {
        question: 'What flooring is best for high-traffic entryways?',
        answer:
          'Choose durable, water-resistant options like porcelain or ceramic tile for easy maintenance, luxury vinyl for comfort and style, natural stone for elegance and longevity, or sealed hardwood for warmth. Include a quality doormat system both outside and inside, consider radiant floor heating for comfort, and use runners or area rugs to protect flooring and add style.',
      },
      {
        question:
          'What furniture pieces are essential for a functional entryway?',
        answer:
          'Include a bench or chair for putting on shoes, a console table or shelf for keys and mail, coat hooks or a coat rack for outerwear, a mirror for last-minute appearance checks, an umbrella stand for wet weather gear, and storage solutions like baskets or drawers for gloves and accessories. Scale furniture appropriately to maintain traffic flow.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'balcony',
    name: 'Balcony',
    // Before -> after loop built by scratchpad/room_pipeline.py: plain room
    // (GPT Image 2 Medium) morphing into the first style (Seedance 2.0 Mini
    // first->last frame), crossfading through two more layout-pinned edits
    // (Nano Banana 2) and back to the before. 720x720, 10.6s, muted.
    heroVideo:
      'https://media.easy-peasy.ai/landing/room-types/balcony-hero.mp4',
    faqs: [
      {
        question: 'How can I make the most of a small balcony?',
        answer:
          'Use folding or stackable furniture for flexibility, choose narrow railing-mounted planters to save floor space, install a fold-down table, utilize vertical space with wall planters or shelving, select multi-functional pieces like storage benches, and use outdoor rugs to define the space.',
      },
      {
        question:
          'What plants work best for balconies with different exposures?',
        answer:
          'For sunny south-facing balconies, choose drought-tolerant plants like lavender, rosemary, and succulents. East/west-facing balconies suit geraniums, petunias, and herbs. North-facing balconies need shade-loving plants like ferns, hostas, and begonias. Consider wind exposure when selecting plants and use heavier pots for stability.',
      },
      {
        question: 'How can I create privacy on my balcony?',
        answer:
          'Install bamboo or reed screening, use tall potted plants like ornamental grasses or columnar trees, hang outdoor curtains or shade sails, add a decorative trellis with climbing plants, or use adjustable side awnings that can be extended when needed.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'rooftop-terrace',
    name: 'Rooftop Terrace',
    faqs: [
      {
        question:
          'What considerations are important for rooftop terrace design?',
        answer:
          'Consider structural load capacity (consult an engineer), water drainage systems, safety barriers/railings, wind exposure (select appropriate furnishings and plants), privacy screening from neighboring buildings, and local building codes or HOA regulations that may affect your design.',
      },
      {
        question: 'What flooring options work best for rooftop terraces?',
        answer:
          'Deck tiles (wood, composite, or porcelain) can be installed over waterproofing membranes, artificial turf provides a green look without soil weight, outdoor porcelain pavers offer durability and style, and interlocking rubber pavers provide safety and comfort. All materials should be weather-resistant and able to withstand temperature extremes.',
      },
      {
        question: 'How can I create shade on my rooftop terrace?',
        answer:
          'Options include retractable awnings that can be adjusted based on sun position, pergolas with climbing plants or fabric covers, large cantilever umbrellas that can be repositioned, sail shades that add architectural interest, and strategically placed tall planters with trees that provide natural shade.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'greenhouse',
    name: 'Greenhouse',
    faqs: [
      {
        question: "What's the ideal orientation and location for a greenhouse?",
        answer:
          'Position your greenhouse to receive maximum southern exposure (northern hemisphere) for optimal sunlight, especially in winter. East-west orientation maximizes light exposure. Place away from trees that cast shadows, near a water source for convenience, and with protection from harsh winds if possible.',
      },
      {
        question: 'What features should a well-designed greenhouse include?',
        answer:
          'Include proper ventilation (roof vents, side vents, or fans), temperature control systems (heaters, shade cloth), adequate shelving and benches at various heights, a water source and drainage system, smooth and washable walking surfaces, supplemental lighting for darker months, and automated systems for watering and environmental control if possible.',
      },
      {
        question: 'How can I manage temperature fluctuations in my greenhouse?',
        answer:
          'Use thermal mass (water barrels, stone pathways) to store heat during the day and release it at night, install insulation for winter (bubble wrap, twin-wall polycarbonate), use shade cloth or whitewash for summer heat reduction, employ automatic vent openers responsive to temperature changes, and consider using a programmable thermostat connected to heating and cooling systems.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'dressing-room',
    name: 'Dressing Room',
    faqs: [
      {
        question: "What's the best layout for a functional dressing room?",
        answer:
          'Design with a central space for dressing, surround with storage organized by clothing type, include a full-length mirror with good lighting, add a seating area for putting on shoes, ensure proper spacing between storage units for easy access (at least 36 inches in walkways), and consider an island or peninsula for folding clothes or displaying accessories.',
      },
      {
        question: 'How do I create perfect lighting for a dressing room?',
        answer:
          'Combine natural light (if possible) with layered artificial lighting including overhead ambient lighting, task lighting around mirrors, interior cabinet lighting, and accent lighting for display areas. Use high CRI (90+) bulbs at 3000-3500K color temperature for accurate color rendering, and install lighting on dimmers for flexibility.',
      },
      {
        question: 'What storage elements are essential in a dressing room?',
        answer:
          'Include hanging space of various heights (double-hang for shirts/pants, single-hang for dresses/coats), drawers for folded items, shelving for shoes and bags, specialized storage for accessories (jewelry drawers, tie/belt racks), hampers for laundry, and closed storage for off-season items. Customize depths based on what\'s being stored (16-24" for hanging clothes, 12-16" for shelving).',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'powder-room',
    name: 'Powder Room',
    faqs: [
      {
        question: 'How can I make a small powder room feel larger?',
        answer:
          'Use a pedestal or wall-mounted sink to free up floor space, install a large mirror to create the illusion of depth, choose light colors for major surfaces, incorporate vertical elements that draw the eye upward, use the same material on the floor and walls for continuity, and ensure adequate lighting to eliminate shadows.',
      },
      {
        question: 'What design elements make a powder room feel luxurious?',
        answer:
          'Incorporate high-quality fixtures in striking finishes, install statement wallpaper or bold tile work, add custom millwork or wainscoting, use elegant lighting like a small chandelier or decorative sconces, include thoughtful accessories like premium hand soap and towels, and consider a distinctive vanity that serves as a focal point.',
      },
      {
        question: 'What are some space-saving solutions for powder rooms?',
        answer:
          'Choose a corner sink to maximize floor space, install floating shelves for decorative storage, use a pocket door or barn door if a standard swing door takes too much space, consider a slim wall-mounted faucet, install towel hooks instead of bars, and look for narrow-depth vanities designed specifically for small spaces (16-18" deep).',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'utility-room',
    name: 'Utility Room',
    faqs: [
      {
        question: 'How should I organize different zones in a utility room?',
        answer:
          'Create dedicated zones for specific functions: cleaning supplies storage, tool storage, household maintenance items, seasonal decorations, bulk purchases, and recycling/waste management. Group similar items together, use adjustable shelving for flexibility, and ensure adequate spacing between zones for easy access.',
      },
      {
        question:
          'What materials and finishes are best for utility room surfaces?',
        answer:
          'Choose durable, moisture-resistant flooring like vinyl, porcelain tile, or sealed concrete. For countertops, consider laminate, stainless steel, or solid surface materials that resist stains and damage. Use semi-gloss or gloss paint on walls for easy cleaning, and select metal or sturdy plastic for shelving that can handle weight and moisture.',
      },
      {
        question: 'How can I maximize storage in a small utility room?',
        answer:
          'Utilize vertical space with floor-to-ceiling shelving, install pegboards or slatwall systems for tools and frequently used items, use the backs of doors with over-door organizers, consider ceiling-mounted storage racks for seasonal items, optimize corners with specialized corner shelving, and label everything clearly for quick identification.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'billiards-room',
    name: 'Billiards Room',
    faqs: [
      {
        question: 'How much space do I need for a proper billiards room?',
        answer:
          "A standard 8-foot table requires a room at least 13'6\" × 17' to allow comfortable playing with full-length cues. For a 9-foot table, allow at least 14'6\" × 18'. Include additional space for seating, storage, and traffic flow. Ceiling height should be at least 7 feet, with light fixtures positioned to not interfere with cue movement.",
      },
      {
        question: "What's the ideal lighting setup for a billiards table?",
        answer:
          "Position a dedicated light fixture 60-66 inches above the table surface, centered over the table with the light covering the entire playing surface but not extending beyond the table edges. Choose fixtures that minimize shadows and glare, with warm lighting (2700-3000K) that doesn't distort ball colors. Include separate ambient lighting for the rest of the room.",
      },
      {
        question:
          'What should I consider when selecting flooring for a billiards room?',
        answer:
          "Choose a stable, level surface that can support the table weight (typically 700-1000 pounds). Hardwood, engineered wood, or quality laminate provide durability and a traditional look. Carpet can work but select a low, dense pile for stability and easy ball retrieval. Consider acoustic properties to manage sound, and ensure the floor finish isn't slippery when chalking cues.",
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'trophy-room',
    name: 'Trophy Room',
    faqs: [
      {
        question:
          'What display options work best for different types of trophies and memorabilia?',
        answer:
          'Use glass-enclosed cabinets for valuable or delicate items, adjustable shelving for items of varying heights, shadow boxes for 3D objects with related memorabilia, wall-mounted display cases for jerseys or larger flat items, and specialized sports equipment mounts (e.g., ball holders, bat racks) for sporting equipment.',
      },
      {
        question: 'How should I approach lighting in a trophy room?',
        answer:
          'Incorporate adjustable accent lighting (track lighting or recessed directional spots) to highlight specific displays, install LED strip lighting inside cabinets for even illumination, use UV-filtered lighting for sensitive items to prevent fading, include dimmers for atmosphere control, and position lighting to minimize glare on glass surfaces.',
      },
      {
        question: 'How can I create an organized display that tells a story?',
        answer:
          'Arrange items chronologically to show progression, group by theme or achievement type, use varying heights and depths to create visual interest, incorporate informational placards or digital displays with background stories, leave negative space to prevent a cluttered appearance, and create a focal point with the most significant or impressive items.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'foyer',
    name: 'Foyer',
    faqs: [
      {
        question: 'What elements are essential for a functional foyer?',
        answer:
          'Include adequate storage for coats and shoes (closet, hooks, or bench with storage), a surface for mail and keys (console table or wall shelf), a mirror for last-minute checks before leaving, durable flooring that can handle traffic and wet footwear, appropriate lighting for both daytime and evening, and seating if space allows (bench or small chair).',
      },
      {
        question: 'How can I make my foyer more welcoming?',
        answer:
          'Add a statement light fixture to create a focal point, incorporate artwork or a gallery wall that reflects your personality, include plants for life and color, use an area rug to define the space and add warmth, choose a door color that makes a statement, and consider a signature scent with a diffuser to create a pleasant first impression.',
      },
      {
        question: "What's the best way to handle storage in a small foyer?",
        answer:
          'Use vertical space with tall, narrow storage units or wall-mounted options, install hooks at different heights for various family members, choose furniture with hidden storage (benches with lids, tables with drawers), utilize the back of the entry door with an over-door organizer, and implement seasonal rotation of items to minimize clutter.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'loft',
    name: 'Loft',
    faqs: [
      {
        question: 'How can I divide an open loft space into functional zones?',
        answer:
          'Use furniture arrangement to create visual boundaries, incorporate room dividers like bookshelves or decorative screens, define areas with different flooring materials or area rugs, create vertical separation with varying ceiling treatments or hanging elements, use lighting to highlight different zones, and maintain a cohesive color scheme throughout for unity.',
      },
      {
        question: "What's the best approach to storage in a loft space?",
        answer:
          'Utilize furniture with built-in storage (platform beds with drawers, storage ottomans), maximize vertical space with tall bookshelves or custom cabinetry, consider built-ins along walls to minimize footprint, use space under stairs if applicable, install closet systems that maximize every inch, and choose multi-functional pieces that serve storage and other needs.',
      },
      {
        question: 'How can I add privacy to sleeping areas in an open loft?',
        answer:
          "Install ceiling-mounted curtain tracks to create wraparound privacy, use folding screens or movable partitions, incorporate partial walls that don't reach the ceiling, position tall furniture strategically as dividers, consider a canopy bed with curtains, or create a dedicated sleeping nook with architectural elements like a slightly raised platform.",
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'man-cave',
    name: 'Man Cave',
    faqs: [
      {
        question:
          'What elements create the perfect man cave for different interests?',
        answer:
          'For sports fans, include memorabilia displays, a quality TV setup, and team colors. For gamers, prioritize comfortable seating, cable management, and ambient lighting. For music enthusiasts, focus on sound system quality, instrument displays, and acoustic treatments. Common elements include comfortable seating, adequate storage, personalized decor, and a dedicated beverage/refreshment area.',
      },
      {
        question: 'How can I improve the acoustics in my man cave?',
        answer:
          'Add soft surfaces like area rugs, upholstered furniture, and curtains to absorb sound, install acoustic panels (available in decorative designs) on walls, use bookcases filled with books as natural diffusers, consider acoustic ceiling treatments if noise transfer is an issue, and use weatherstripping around doors to minimize sound leakage to other areas.',
      },
      {
        question:
          "What's the ideal seating arrangement for entertainment and socializing?",
        answer:
          'Position primary seating with a clear view of the TV or focal point, arrange additional seating to facilitate conversation (L-shapes or U-shapes work well), include varying seating heights for different activities, ensure adequate space between pieces for movement and drink placement, incorporate reclining options for comfort, and consider modular pieces that can be reconfigured for different activities.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'she-shed',
    name: 'She Shed',
    faqs: [
      {
        question: 'How can I make my she-shed comfortable year-round?',
        answer:
          'Insulate walls, ceiling, and floor properly, install weatherstripping around doors and windows, consider a small HVAC unit or mini-split system for temperature control, use a ceiling fan for air circulation, add a small space heater or portable air conditioner for seasonal adjustments, and include cozy textiles like rugs and throws for added warmth and comfort.',
      },
      {
        question: 'What utilities should I consider installing in my she-shed?',
        answer:
          'Prioritize electricity for lighting, climate control, and devices (consult a professional for safe installation), consider plumbing if your activities require water, install adequate outlets throughout the space, plan lighting carefully with a combination of ambient, task, and accent fixtures, and explore WiFi extenders or a dedicated line if internet connectivity is important.',
      },
      {
        question: 'How can I design my she-shed to serve multiple purposes?',
        answer:
          'Create flexible zones that can adapt to different activities, incorporate furniture that serves multiple functions or can be easily moved, install modular storage that can be reconfigured, use a desk or table that can handle various projects, include adequate wall space for interchangeable tools or equipment, and consider foldable or nestable furniture that can be stored when not needed.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'ensuite-bathroom',
    name: 'Ensuite Bathroom',
    faqs: [
      {
        question: "What's the ideal layout for an ensuite bathroom?",
        answer:
          'Position the toilet with privacy in mind (behind a door or half wall), place the shower or bath where it has a focal point view if possible, ensure adequate clearance around all fixtures (at least 30" in front of fixtures), consider the door swing to maximize space, plan for sufficient storage near where items will be used, and create a clear path from the bedroom that doesn\'t expose the entire bathroom.',
      },
      {
        question: 'How can I create a spa-like feel in my ensuite bathroom?',
        answer:
          'Include a rainfall shower head or deep soaking tub, use natural materials like stone or wood, incorporate plants for a living element, install dimmable lighting for ambiance, add a heated floor for comfort, choose a calming color palette based on nature, minimize visual clutter with adequate storage, and include luxury touches like plush towels and high-quality bath products.',
      },
      {
        question: 'What storage solutions work best in an ensuite bathroom?',
        answer:
          'Install recessed medicine cabinets to maximize wall space, include vanity drawers rather than cabinets for easier access, incorporate built-in niches in shower areas, use vertical storage with tall cabinets for towels and supplies, consider hidden storage behind mirrors, add hooks or towel warmers for daily use items, and include dedicated makeup/grooming stations with appropriate storage if space allows.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'breakfast-nook',
    name: 'Breakfast Nook',
    faqs: [
      {
        question: "What's the ideal size and shape for a breakfast nook?",
        answer:
          'Allow at least 30" from the edge of the table to the wall for comfortable seating and movement. For rectangular tables, 36" is ideal. L-shaped banquette seating works well in corners, while U-shaped maximizes seating in a recessed area. Round tables work best for square spaces, while rectangular or oval tables suit longer, narrower areas.',
      },
      {
        question:
          'How can I make my breakfast nook more comfortable and inviting?',
        answer:
          'Add cushions and pillows to banquette seating, ensure adequate back support with proper cushion depth and height, incorporate a variety of textures for visual and tactile interest, position the nook near natural light if possible, add plants for life and color, include convenient outlets for devices or small appliances, and consider adjustable lighting for different times of day.',
      },
      {
        question:
          'What storage options can I incorporate into a breakfast nook?',
        answer:
          "Use built-in bench seating with hinged tops or drawers underneath, install shallow cabinets above or beside the seating area, add floating shelves on walls for display and storage, incorporate a storage ottoman that can double as extra seating, choose a table with drawers or a lower shelf, and include hooks or baskets for daily items like napkins, placemats, or children's items.",
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'covered-porch',
    name: 'Covered Porch',
    faqs: [
      {
        question:
          'How can I make my covered porch usable in different seasons?',
        answer:
          'Install ceiling fans for summer air circulation, add outdoor heaters or a fireplace for cooler months, use weatherproof curtains or blinds to block wind and rain, consider retractable screens to keep insects out while maintaining airflow, include adequate lighting for evening use year-round, and choose furniture and cushions with weather-resistant, quick-drying materials.',
      },
      {
        question: 'What flooring options are best for a covered porch?',
        answer:
          'Weather-resistant options include: porcelain or ceramic tile with a slip-resistant finish, composite decking that resists moisture and fading, pressure-treated wood with proper sealing, natural stone like slate or flagstone for durability, and outdoor-rated concrete pavers. All should be properly sloped for drainage and sealed appropriately for your climate.',
      },
      {
        question: 'How should I arrange furniture on my covered porch?',
        answer:
          "Create distinct zones for different activities (dining, conversation, relaxation), position seating to take advantage of views while maintaining conversation flow, ensure adequate circulation space (at least 36\" for pathways), include side tables within arm's reach of seating, consider the sun's path throughout the day, and arrange furniture for both intimate gatherings and larger groups if space allows.",
      },
      ...commonFaqs,
    ],
  },
  // { slug: 'kitchen-living-combo', name: 'Kitchen Living Combo' },
  {
    slug: 'tool-shed',
    name: 'Tool Shed',
    faqs: [
      {
        question: "What's the most efficient way to organize tools in a shed?",
        answer:
          'Implement a wall organization system using pegboards, slatwall, or tool racks, group similar tools together by function, store frequently used items at eye level, use clear bins with labels for small parts, hang long-handled tools on hooks or specialized racks, include adequate workbench space, and establish a "return after use" policy to maintain organization.',
      },
      {
        question: 'How can I maximize storage in a small tool shed?',
        answer:
          'Utilize all vertical space with wall-mounted storage up to the ceiling, install overhead racks for seasonal or less-used items, use the backs of doors with door-mounted organizers, incorporate modular shelving that can be reconfigured as needs change, consider fold-down workspaces that can be stowed when not in use, and use magnetic strips for small metal tools.',
      },
      {
        question: 'What features should I include for a functional tool shed?',
        answer:
          'Ensure adequate lighting (both natural and artificial), install durable flooring that can withstand heavy tools and spills, incorporate a workbench at the proper height (typically 36-38"), provide electrical outlets for power tools, include ventilation to prevent moisture buildup and fume accumulation, consider climate control for temperature-sensitive tools or materials, and add a lockable cabinet for hazardous materials.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'cabana',
    name: 'Cabana',
    faqs: [
      {
        question:
          'What elements make a pool cabana both functional and stylish?',
        answer:
          'Include changing areas with privacy, convenient towel storage and hooks, comfortable seating for relaxation, shade options for sun protection, durable surfaces that can handle moisture, a small refrigerator for refreshments, good ventilation, outdoor shower facilities if possible, and weather-resistant furniture and textiles that complement your outdoor design theme.',
      },
      {
        question:
          'What materials work best for cabana construction and furnishings?',
        answer:
          'Choose water-resistant flooring like porcelain tile or composite decking, select mildew-resistant fabrics and cushions specifically designed for outdoor use, use cedar, teak or other naturally rot-resistant woods for structural elements (or treated lumber), incorporate fade-resistant curtains or screens for privacy, and select metals with proper finishes to prevent rust in high-humidity environments.',
      },
      {
        question:
          'How can I design a cabana that transitions from day to night use?',
        answer:
          'Install versatile lighting with dimmers or separate circuits (string lights, recessed lighting, and lanterns), incorporate a small firepit or outdoor heater for evening warmth, include comfortable seating that works for both lounging and socializing, add Bluetooth speakers for ambiance, consider convertible furniture that can transition from daytime lounging to evening dining, and use indoor/outdoor rugs to define spaces.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'yoga-studio',
    name: 'Yoga Studio',
    faqs: [
      {
        question: 'What flooring is best for a home yoga studio?',
        answer:
          'Ideal options include: cork (naturally antimicrobial with good cushioning), engineered hardwood with proper underlayment for stability and warmth, bamboo for sustainability and resilience, specialized rubber flooring designed for exercise spaces, or even wall-to-wall low-pile carpet if you prefer more cushioning. Avoid slippery surfaces and ensure the flooring can withstand regular cleaning.',
      },
      {
        question: 'How should I approach lighting and color in a yoga studio?',
        answer:
          "Use adjustable lighting with dimmers for different practice styles and times of day, maximize natural light where possible, avoid direct overhead lighting that shines in practitioners' eyes when lying down, choose calming colors inspired by nature (soft blues, greens, neutrals), minimize visual distractions with simple decor, and consider color psychology principles (blues for calm, yellows for energy).",
      },
      {
        question:
          'What elements create the ideal atmosphere for yoga practice?',
        answer:
          'Focus on good air quality with adequate ventilation, incorporate natural elements like plants or wood, ensure temperature control for comfort (typically 68-70°F), include storage for props that maintains a clutter-free environment, add a water station for hydration, consider acoustic treatments for sound quality during guided practice, and include a small altar or focal point for meditation if aligned with your practice.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'storage-room',
    name: 'Storage Room',
    faqs: [
      {
        question: 'How can I organize my storage room for maximum efficiency?',
        answer:
          'Group similar items together, create a cataloging system with labels and inventory list, store seasonal items in clearly marked containers, position frequently accessed items in easy-to-reach locations, use vertical space with adjustable shelving, leave adequate aisle space for access (minimum 36"), and implement a rotation system for items with expiration dates or time-sensitive relevance.',
      },
      {
        question:
          'What shelving and storage systems work best for different items?',
        answer:
          'Heavy-duty adjustable metal shelving works well for most household items, plastic bins with lids protect contents from dust and pests, vacuum-sealed bags reduce volume for textiles and clothing, specialty racks for sporting equipment or tools maximize space, clear containers allow visibility of contents, and modular systems can grow or adapt as your storage needs change.',
      },
      {
        question: 'How can I protect items in long-term storage?',
        answer:
          'Control humidity with a dehumidifier in damp environments, keep items elevated off the floor to prevent moisture damage, use acid-free materials for storing photos and important documents, protect textile items from moths with proper repellents, cover furniture with breathable fabric covers rather than plastic, use climate-controlled storage for sensitive electronics or valuables, and establish a regular schedule for checking and maintaining stored items.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'sewing-room',
    name: 'Sewing Room',
    faqs: [
      {
        question: "What's the ideal layout for a functional sewing room?",
        answer:
          'Create distinct zones for different activities: cutting area with large flat surface, sewing machine station with adequate electrical outlets, pressing area with ironing board and iron, storage for fabrics and notions, and design wall or space for planning projects. Position the sewing machine with space behind for fabric drape and arrange for smooth workflow between cutting, sewing, and pressing.',
      },
      {
        question: 'What lighting is essential for a sewing room?',
        answer:
          'Layer lighting with bright, shadow-free overhead lighting (5000-6500K daylight bulbs), task lighting directly at the sewing machine and cutting table, adjustable desk lamps that can be positioned precisely, natural light for color accuracy (with window treatments to control glare), and consider magnifying lamps for detailed handwork or machine threading.',
      },
      {
        question: 'How can I organize fabric and sewing supplies effectively?',
        answer:
          'Store fabric visible on shelves or in clear containers sorted by type/color, use thread racks for visible access to colors, implement small drawer systems for notions organized by type, keep specialty tools on pegboards or in labeled containers, store patterns in acid-free folders or hanging files, use magnetic strips for small metal items, and incorporate a project bin system for works-in-progress.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'classroom',
    name: 'Classroom',
    // Before -> after loop built by scratchpad/room_pipeline.py: plain room
    // (GPT Image 2 Medium) morphing into the first style (Seedance 2.0 Mini
    // first->last frame), crossfading through two more layout-pinned edits
    // (Nano Banana 2) and back to the before. 720x720, 10.6s, muted.
    heroVideo:
      'https://media.easy-peasy.ai/landing/room-types/classroom-hero.mp4',
    faqs: [
      {
        question:
          'How can I arrange seating for different learning activities?',
        answer:
          'Use flexible furniture that can be reconfigured: clusters for collaborative work, U-shape for discussions, rows for focused individual work or testing, and floor seating areas for casual reading or gatherings. Include a mix of seating types to accommodate different learning styles and physical needs, and ensure clear sightlines to teaching areas from all positions.',
      },
      {
        question: 'What storage solutions work best in a classroom setting?',
        answer:
          'Implement labeled bins or cubbies for student supplies and materials, use color-coding systems for different subjects or age groups, include lockable storage for valuable equipment or confidential items, install wall-mounted storage to maximize floor space, incorporate open shelving for frequently accessed resources, and create systems for distributing and collecting materials efficiently.',
      },
      {
        question:
          'How can I create different learning zones within a classroom?',
        answer:
          'Define areas using rugs, furniture arrangement, or low dividers, create a quiet reading corner with comfortable seating and good lighting, designate a technology zone with appropriate power and equipment, include a hands-on discovery or maker space, establish a group work area with collaborative surfaces, and use visual cues like signage or color to identify different functional zones.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'church',
    name: 'Church',
    faqs: [
      {
        question: 'What considerations are important for worship space layout?',
        answer:
          'Ensure clear sightlines to focal points like altars or podiums, create adequate circulation paths for movement during services, consider acoustics for both spoken word and music, position seating to facilitate community interaction while maintaining focus on worship elements, provide accessible routes for all participants, and balance intimate connection with capacity requirements.',
      },
      {
        question: 'How can lighting enhance the worship experience?',
        answer:
          'Layer lighting with ambient illumination for the entire space, focused lighting on worship leaders and presentation areas, adjustable systems to create different moods for various service types, accent lighting for architectural features or sacred elements, natural light managed with appropriate window treatments, and proper illumination of paths and exits for safety.',
      },
      {
        question:
          'What acoustical elements should be considered in worship space design?',
        answer:
          'Balance sound reflection (for music resonance) with absorption (for speech clarity), position sound systems strategically based on professional acoustic analysis, incorporate architectural elements that enhance natural sound distribution, consider acoustic panels disguised as decorative elements where needed, use appropriate flooring materials to manage sound, and create zones with different acoustic properties for various functions.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'waiting-room',
    name: 'Waiting Room',
    faqs: [
      {
        question:
          'How can I make a waiting room more comfortable and less stressful?',
        answer:
          'Include varied seating options for different body types and needs, provide adequate personal space between seating areas, incorporate natural elements like plants or natural light, choose a calming color palette, offer distractions like magazines or subtle media, reduce noise with acoustic treatments, ensure good air quality and temperature control, and use wayfinding elements to reduce confusion.',
      },
      {
        question:
          'What seating arrangements work best for different types of waiting rooms?',
        answer:
          'For healthcare settings, arrange seating in small groupings with adequate separation for privacy and infection control. For family-oriented spaces, create defined areas for children and adults. For business settings, include both collaborative seating and private options. Generally, avoid forcing eye contact between waiting individuals and include accessible seating prominently, not as an afterthought.',
      },
      {
        question:
          'How can I incorporate technology effectively in a waiting area?',
        answer:
          'Provide easily accessible charging stations for devices, offer clear WiFi connection information, position digital displays at comfortable viewing heights and angles, include technology that serves a purpose (informational or entertainment), consider self-service check-in kiosks positioned for privacy, integrate cable management for safety, and balance technology with non-digital options for those who prefer them.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'reception-area',
    name: 'Reception Area',
    faqs: [
      {
        question:
          'What elements create an impactful first impression in a reception area?',
        answer:
          "Include a clearly visible reception desk positioned to acknowledge arrivals immediately, incorporate branding elements like logo signage or brand colors, ensure the space reflects your organization's values and identity, create a focal point with artwork or architectural features, use appropriate lighting to highlight key areas, maintain immaculate cleanliness, and consider the sensory experience including pleasant scents and comfortable acoustics.",
      },
      {
        question:
          'How should I design the reception desk area for functionality?',
        answer:
          'Position the desk to allow staff to see arrivals while maintaining security, include both standing and seated interaction heights if possible, provide adequate workspace hidden from visitor view, incorporate technology infrastructure for computers and communications, ensure accessibility for all users, include storage for frequently needed items, and consider sound privacy for conversations with visitors.',
      },
      {
        question: 'What seating options work best in reception areas?',
        answer:
          'Provide a mix of seating types to accommodate different needs and wait times: individual seats for privacy, small groupings for those arriving together, durable and stain-resistant upholstery for high-traffic areas, adequate arm support for ease of standing, appropriate seat height and depth for comfort (17-19" height typically works well), and convenient side tables for personal items.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'hall',
    name: 'Hall',
    faqs: [
      {
        question:
          'How can I make a narrow hallway feel wider and more spacious?',
        answer:
          'Use light colors on walls to visually expand the space, incorporate strategic lighting that draws the eye forward, hang mirrors to create the illusion of width, keep floor space clear of obstacles, choose appropriately scaled artwork and decor, install crown molding to draw the eye upward, and consider recessed storage rather than furniture that projects into the space.',
      },
      {
        question: 'What lighting options work best for hallways?',
        answer:
          'Layer lighting with ambient overhead fixtures for general illumination, wall sconces to add width perception and eliminate shadows, recessed lighting for a clean ceiling line in narrow spaces, motion-sensor options for convenience and energy efficiency, picture lights to highlight artwork and create visual interest, and night lighting for safety during evening hours.',
      },
      {
        question: 'How can I add functional storage to a hallway?',
        answer:
          'Install shallow built-in cabinets or recessed niches between wall studs, use narrow console tables with drawers or shelves below, incorporate bench seating with hidden storage compartments, mount floating shelves for decorative and functional items, utilize vertical space with hooks or track systems, and consider ceiling-height cabinetry for seasonal or rarely used items in wider hallways.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'backyard',
    name: 'Backyard',
    faqs: [
      {
        question: 'How can I design a backyard that serves multiple purposes?',
        answer:
          'Create distinct zones for different activities: entertaining, gardening, relaxation, and play. Use elements like pergolas, plantings, or hardscaping to define spaces. Incorporate flexible furniture that can serve multiple functions, consider seasonal usage patterns, and plan for both active and quiet areas with smooth transitions between zones.',
      },
      {
        question:
          'What are the best landscaping options for low-maintenance backyards?',
        answer:
          'Choose native plants that thrive in your climate without extra watering or care, install hardscaping elements like patios or pathways for year-round appeal, use perennial plants that return annually, incorporate mulch to reduce weeding and retain moisture, consider drought-resistant ground covers instead of traditional lawns, and group plants with similar water and care needs together.',
      },
      {
        question: 'How can I create privacy in my backyard?',
        answer:
          'Install fencing appropriate to your style and budget (wood, vinyl, or composite), plant fast-growing privacy trees or tall shrubs along property lines, use trellises with climbing plants for natural screening, add outdoor curtains or shade structures for temporary privacy, create raised garden beds as natural barriers, and consider the mature size of plants when planning for long-term privacy.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'meditation-room',
    name: 'Meditation Room',
    faqs: [
      {
        question:
          'What are the essential elements for creating a calming meditation room?',
        answer:
          'Focus on minimal clutter with clean, open spaces. Choose soft, neutral colors like sage green, warm whites, or soft grays. Incorporate natural materials like wood, bamboo, or stone. Add comfortable floor cushions or a meditation bench, soft lighting with dimmers, and consider sound elements like a small water fountain or white noise machine. Keep decorations minimal but meaningful.',
      },
      {
        question:
          'How can I create a meditation space in a small room or corner?',
        answer:
          'Even a 4x4 foot corner can become a meditation sanctuary. Use a room divider or curtain to define the space, add a comfortable cushion or small bench, incorporate vertical storage for meditation aids, use wall-mounted shelves for candles or plants, and consider a fold-away meditation altar. Focus on creating visual calm through consistent colors and minimal decor.',
      },
      {
        question:
          'What lighting and sound considerations are important for meditation rooms?',
        answer:
          'Install dimmable lights or use multiple light sources like salt lamps, candles, or soft LED strips. Natural light is ideal but should be diffused with sheer curtains. For sound, ensure good insulation from outside noise, consider a white noise machine or nature sounds, add soft textiles to absorb echo, and position the space away from high-traffic areas of your home.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'content-creator-studio',
    name: 'Content Creator Studio',
    faqs: [
      {
        question:
          'What is the ideal lighting setup for a content creator studio?',
        answer:
          'Use a three-point lighting system: key light (main source at 45 degrees), fill light (softer, opposite side), and background light for depth. Ring lights work well for face-focused content. Install adjustable LED panels for consistent color temperature. Consider RGB lights for creative effects. Position lights to avoid shadows and ensure even coverage across your filming area.',
      },
      {
        question: 'How can I optimize acoustics in my content creation space?',
        answer:
          'Add acoustic panels on walls to reduce echo and improve sound quality. Use thick rugs and soft furnishings to absorb sound. Install foam tiles on ceiling if needed. Position your setup away from windows and noisy appliances. Consider a vocal booth or portable sound shield for recording. Use heavy curtains to block external noise and create better acoustics.',
      },
      {
        question:
          'What background elements work best for streaming and video content?',
        answer:
          'Create a clean, branded background that reflects your content style. Use adjustable shelving to display relevant items without clutter. Consider a green screen for versatility. Add subtle LED strips or neon signs for visual interest. Keep backgrounds at least 3-4 feet behind you for proper depth. Ensure your background enhances rather than distracts from your content.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'adu-suite',
    name: 'ADU / Mother-in-Law Suite',
    faqs: [
      {
        question:
          'How can I maximize space efficiency in an ADU or mother-in-law suite?',
        answer:
          'Use multi-functional furniture like murphy beds, expandable dining tables, and storage ottomans. Create open floor plans with defined zones using area rugs and lighting. Install built-in storage solutions and utilize vertical space with tall shelving. Choose compact appliances and consider a kitchenette instead of a full kitchen. Use pocket doors to save floor space.',
      },
      {
        question:
          'What are the key considerations for ADU bathroom and kitchen design?',
        answer:
          'For bathrooms, install grab bars, use non-slip flooring, ensure adequate lighting, and consider a walk-in shower with bench. For kitchens, include essential appliances in compact sizes, use pull-out drawers for easy access, ensure counter heights are comfortable, and provide adequate task lighting. Both spaces should prioritize accessibility and safety.',
      },
      {
        question:
          'How can I ensure privacy while maintaining connection to the main house?',
        answer:
          'Create a separate entrance with its own outdoor space or patio. Use soundproofing materials in shared walls. Install separate HVAC controls for comfort independence. Design windows to maximize natural light while maintaining privacy with strategic placement or frosted glass. Consider a small foyer or entry area to create psychological separation from the main house.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'flex-space',
    name: 'Multi-Functional / Flex Space',
    faqs: [
      {
        question:
          'How can I design a room that easily transitions between different functions?',
        answer:
          'Use modular furniture on wheels for easy reconfiguration. Install multiple lighting options for different moods and tasks. Choose neutral base colors with changeable accents. Use room dividers, curtains, or sliding panels to create flexible zones. Invest in convertible furniture like desk-to-dining tables or sofa beds. Plan storage that works for all intended uses.',
      },
      {
        question:
          'What storage solutions work best for multi-functional spaces?',
        answer:
          'Install wall-mounted storage systems that can be adjusted as needs change. Use closed storage like cabinets or storage benches to hide items when switching functions. Label containers clearly for quick transitions. Consider a closet organization system with adjustable components. Use vertical space with tall bookcases or shelving units that can serve as room dividers.',
      },
      {
        question:
          'How do I handle technology and electrical needs in a flex space?',
        answer:
          'Install multiple outlets around the perimeter and consider floor outlets for central areas. Use cable management systems to keep cords organized and hidden. Set up a central charging station for devices. Consider smart home technology for easy lighting and climate control. Plan for both work technology needs (monitors, printers) and entertainment systems in your layout.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'reading-nook',
    name: 'Reading Nook',
    faqs: [
      {
        question:
          'What are the essential elements for creating a cozy reading nook?',
        answer:
          'Start with comfortable seating like a plush armchair, chaise lounge, or built-in bench with cushions. Add proper task lighting such as a floor lamp or adjustable reading light. Include a side table for books and beverages. Add soft textiles like throw blankets and pillows. Consider a small bookshelf within reach and a footstool for ultimate comfort.',
      },
      {
        question:
          'How can I create a reading nook in a small space or unused corner?',
        answer:
          'Transform awkward spaces like under-stair areas, bay windows, or wide hallways. Use a corner with a comfortable chair and ottoman. Install floating shelves for books without taking floor space. Add a wall-mounted light to save table space. Use a pouf instead of a side table for flexibility. Define the space with a small area rug.',
      },
      {
        question:
          'What lighting is best for a reading nook to prevent eye strain?',
        answer:
          'Position lights over your shoulder to illuminate pages without shadows. Use warm white bulbs (2700-3000K) for comfortable reading. Install adjustable task lighting with at least 450 lumens. Consider multiple light sources to reduce contrast. Add ambient lighting for overall comfort. Natural light is ideal but use window treatments to control glare.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'wellness-room',
    name: 'Wellness / Spa Room',
    faqs: [
      {
        question:
          'What elements create a spa-like atmosphere in a home wellness room?',
        answer:
          'Use a calming color palette with soft neutrals, blues, or greens. Install dimmable lighting and consider chromotherapy options. Add aromatherapy with essential oil diffusers or candles. Include natural materials like bamboo, teak, or stone. Provide plush towels, robes, and comfort items. Consider a sound system for relaxing music or nature sounds.',
      },
      {
        question: 'How can I set up different wellness zones in one room?',
        answer:
          'Designate areas for different activities: massage table or comfortable lounger for treatments, yoga/stretching space with mirrors and mats, meditation corner with cushions, and storage for wellness equipment. Use room dividers or curtains for privacy between zones. Ensure each area has appropriate lighting and ventilation.',
      },
      {
        question: 'What features should I consider for a home wellness room?',
        answer:
          'Install a mini-fridge for cold towels and beverages. Add a towel warmer for luxury. Consider a small sink for convenience. Include storage for oils, lotions, and equipment. Install good ventilation for aromatherapy. Add a water feature for ambiance. Consider heated floors for comfort. Include plants for air purification and natural beauty.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'music-room',
    name: 'Music Listening Room',
    faqs: [
      {
        question: 'How do I optimize acoustics in a music listening room?',
        answer:
          'Position speakers at ear level forming an equilateral triangle with your listening position. Add bass traps in corners to control low frequencies. Install acoustic panels at first reflection points on walls. Use diffusers on the rear wall to scatter sound waves. Include soft furnishings to prevent excessive reverb. Avoid parallel walls if possible or treat them acoustically.',
      },
      {
        question:
          'What furniture and layout work best for a music listening room?',
        answer:
          'Center your primary listening chair at the optimal sweet spot between speakers. Add comfortable seating for multiple listeners slightly behind the sweet spot. Include storage for vinyl records, CDs, or equipment. Use a dedicated audio rack for components. Consider a bar cart for refreshments. Add side tables for convenience without interfering with sound.',
      },
      {
        question:
          'How should I handle equipment and cable management in a music room?',
        answer:
          'Use quality speaker stands or wall mounts for optimal positioning. Install dedicated electrical circuits for audio equipment to reduce interference. Use cable management systems to organize and hide wires. Position equipment racks for easy access and proper ventilation. Consider room treatment that accommodates cable runs. Label all connections for easy troubleshooting.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'home-gym',
    name: 'Home Gym',
    faqs: [
      {
        question: 'How to design a home gym on a $2000 budget?',
        answer:
          'Focus on essential equipment like adjustable dumbbells ($200-300), resistance bands ($50), yoga mats ($100), and a stability ball ($50). Use existing space or convert a garage/basement. Add mirrors for form checking ($200), proper flooring for safety ($300-500), and basic storage solutions ($100). Prioritize quality basics over trendy equipment.',
      },
      {
        question: 'Best flooring options for gym spaces',
        answer:
          'Rubber interlocking tiles provide excellent cushioning and joint protection, foam mats are affordable and easy to store, luxury vinyl plank (LVP) offers durability and style, and carpet tiles with high density work well for cardio areas. Avoid hardwood or tile without proper underlayment to prevent injury.',
      },
      {
        question: 'How to integrate smart fitness technology into home gyms',
        answer:
          'Install smart mirrors for guided workouts, connect fitness trackers to TV displays, use smart scales for body composition tracking, add voice-activated lighting controls, incorporate smart water bottles that track hydration, and use apps that sync with your smart home system for automated workout lighting and music.',
      },
      {
        question: 'Home gym ventilation and air quality considerations',
        answer:
          'Install ceiling fans or exhaust fans for air circulation, use dehumidifiers to control moisture from sweat, add air purifiers to remove workout odors, ensure windows for natural ventilation, use moisture-resistant materials throughout, and consider carbon monoxide detectors if using gas equipment.',
      },
      {
        question: 'How to make home gyms family-friendly',
        answer:
          'Designate equipment storage areas that can be hidden behind curtains or doors, choose multi-functional equipment that serves different purposes, add comfortable seating for rest periods, include kids-friendly equipment like jump ropes or resistance bands, and create a welcoming atmosphere with plants and natural light.',
      },
      {
        question: 'Sustainable home gym equipment and materials',
        answer:
          'Choose equipment made from recycled materials, opt for durable, long-lasting pieces over disposable ones, use bamboo or cork flooring, select energy-efficient lighting, choose water bottles made from recycled plastic, and look for brands that use sustainable manufacturing practices.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'pet-room',
    name: 'Pet Room',
    faqs: [
      {
        question: 'How to design a dedicated pet room for dogs and cats',
        answer:
          "Create separate zones for feeding, sleeping, and play areas. Use pet-safe flooring that's easy to clean, install scratching posts and climbing structures for cats, provide comfortable beds and crates for dogs, add storage for toys and supplies, and include a washing station for cleaning muddy paws.",
      },
      {
        question: 'Best materials for pet-friendly room design',
        answer:
          'Choose durable fabrics like microfiber or performance fabric for upholstery, use washable rugs that resist pet hair and stains, opt for scratch-resistant surfaces like quartz countertops, select pet-safe paints and finishes, and use easy-to-clean materials like luxury vinyl plank flooring.',
      },
      {
        question: 'How to integrate pet technology into room design',
        answer:
          'Install automatic feeders and water bowls, add smart pet doors that grant access based on collar tags, incorporate pet cameras for monitoring, use smart litter boxes with app notifications, add automatic toy dispensers, and consider smart collars that integrate with room automation.',
      },
      {
        question: 'Pet room storage and organization solutions',
        answer:
          'Use wall-mounted shelves for supplies, install toy bins and storage ottomans, add closet organizers for grooming tools, create designated food storage areas, use drawer systems for medications and documents, and incorporate vertical storage to maximize space.',
      },
      {
        question: 'How to create safe pet sleeping areas',
        answer:
          'Provide elevated beds away from drafts, use orthopedic mattresses for older pets, create enclosed spaces for security, ensure proper temperature control, add soft lighting for nighttime comfort, and choose non-toxic materials that are safe for pets to chew on.',
      },
      {
        question: 'Sustainable pet room design ideas',
        answer:
          'Use recycled materials for furniture, choose eco-friendly pet toys and bedding, install energy-efficient lighting, use natural cleaning products, opt for sustainable wood sources, and incorporate indoor plants that are safe for pets.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'craft-room',
    name: 'Craft Room / Creative Studio',
    faqs: [
      {
        question: 'How to organize a craft room for multiple hobbies',
        answer:
          'Create dedicated workstations for different activities like sewing, painting, and scrapbooking. Use labeled storage bins for supplies, install pegboards for tools, add rolling carts for mobile storage, create wall-mounted shelves for materials, and use drawer organizers for small items.',
      },
      {
        question: 'Best lighting setup for craft and creative work',
        answer:
          'Install adjustable task lighting over work surfaces, use daylight-simulating bulbs for color accuracy, add LED strip lighting under cabinets, incorporate natural light with adjustable blinds, use magnifying lamps for detailed work, and ensure proper lighting for both close-up and overview tasks.',
      },
      {
        question: 'Craft room storage solutions for small spaces',
        answer:
          'Utilize vertical space with floor-to-ceiling shelving, use over-door organizers for supplies, install wall-mounted tool racks, add under-table storage drawers, use stackable bins for seasonal items, and incorporate multi-level work surfaces with storage underneath.',
      },
      {
        question: 'How to integrate smart technology into craft rooms',
        answer:
          'Add smart lighting that adjusts based on time of day, use digital inventory systems for supplies, install smart plugs for equipment control, incorporate tablet stands for tutorials, use voice-activated assistants for hands-free operation, and add smart storage solutions with inventory tracking.',
      },
      {
        question: 'Sustainable craft room materials and practices',
        answer:
          'Use recycled containers for storage, choose bamboo or reclaimed wood for furniture, opt for energy-efficient lighting, use eco-friendly paints and glues, recycle packaging materials, and choose sustainable sources for art supplies.',
      },
      {
        question: 'Craft room ventilation and air quality considerations',
        answer:
          'Install exhaust fans for fume removal, use air purifiers to filter paint and glue particles, ensure proper ventilation for spray work, add carbon filters for solvent-based materials, use natural ventilation with windows, and choose low-VOC materials whenever possible.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'recording-studio',
    name: 'Recording Studio',
    faqs: [
      {
        question: 'How to set up a recording studio on a budget',
        answer:
          'Start with a quality microphone ($100-300), audio interface ($150-400), headphones ($50-150), and acoustic foam panels ($100-200). Use a quiet room or closet, add sound blankets for additional soundproofing, and use free or low-cost recording software. Focus on good acoustics before expensive equipment.',
      },
      {
        question: 'Best acoustic treatments for recording spaces',
        answer:
          'Install acoustic foam panels on walls and ceiling, use bass traps in corners, add diffuser panels for sound scattering, use thick rugs or carpets for floor sound absorption, install heavy curtains for additional sound dampening, and consider acoustic door seals.',
      },
      {
        question: 'Recording studio lighting and ambiance considerations',
        answer:
          'Use soft, diffused lighting to reduce glare on screens, install adjustable task lighting for mixing, add warm ambient lighting for comfort during long sessions, use LED lights to minimize heat and noise, consider colored accent lighting for mood, and ensure proper lighting for video recording if needed.',
      },
      {
        question: 'How to integrate smart technology into recording studios',
        answer:
          'Use smart lighting that adjusts for different recording modes, install automated acoustic treatments, add voice-activated controls for equipment, use smart plugs for powering equipment on/off, incorporate streaming integration for remote recording, and add smart climate control for optimal recording conditions.',
      },
      {
        question: 'Recording studio storage and cable management',
        answer:
          'Use cable management solutions like sleeves and organizers, install wall-mounted racks for equipment, add drawer systems for accessories, use under-desk cable trays, incorporate labeled storage for different cable types, and create designated spaces for microphones and accessories.',
      },
      {
        question: 'Sustainable recording studio design practices',
        answer:
          'Choose energy-efficient equipment and lighting, use recycled materials for acoustic treatments, opt for durable, long-lasting equipment, recycle old cables and equipment, use low-power consumption devices, and choose sustainable sources for acoustic materials.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'plant-room',
    name: 'Plant Room / Indoor Garden',
    faqs: [
      {
        question: 'How to design an indoor plant room with proper lighting',
        answer:
          'Install grow lights with timers and adjustable intensity, use LED lights for energy efficiency, position lights 6-12 inches above plants, create different light zones for different plant types, use reflective surfaces to maximize light distribution, and ensure proper ventilation to prevent mold.',
      },
      {
        question: 'Best irrigation systems for indoor plant rooms',
        answer:
          'Install drip irrigation systems for consistent watering, use self-watering planters for low maintenance, add humidity trays for tropical plants, incorporate automated watering systems with timers, use capillary mats for passive watering, and consider hydroponic systems for efficient space use.',
      },
      {
        question: 'Plant room ventilation and humidity control',
        answer:
          'Install exhaust fans for air circulation, use dehumidifiers to control moisture levels, add humidity trays or misting systems for tropical plants, ensure proper air flow between plants, use carbon filters if needed, and monitor temperature and humidity with smart sensors.',
      },
      {
        question: 'Sustainable plant room materials and practices',
        answer:
          'Use recycled containers and planters, choose bamboo or reclaimed wood for shelving, opt for energy-efficient grow lights, use organic potting soil, recycle water when possible, choose drought-tolerant plants, and use natural pest control methods.',
      },
      {
        question: 'How to organize different plant types in one space',
        answer:
          'Group plants by light and water requirements, create zones for different plant families, use adjustable shelving for vertical growth, incorporate plant walls for space efficiency, add identification labels for plant care, and create pathways for easy access to all plants.',
      },
      {
        question: 'Plant room storage solutions for supplies and tools',
        answer:
          'Use wall-mounted tool racks for gardening supplies, add storage bins for soil and fertilizers, install drawer systems for small tools, create potting stations with built-in storage, use magnetic strips for metal tools, and incorporate under-shelf storage for supplies.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'safe-room',
    name: 'Safe Room / Panic Room',
    faqs: [
      {
        question: 'How to design a discreet yet functional safe room',
        answer:
          'Conceal the room behind bookcases or closets, use soundproofing materials for privacy, install reinforced doors with multiple locking mechanisms, add communication systems for emergency contact, ensure adequate ventilation and lighting, and include comfortable seating for extended stays.',
      },
      {
        question: 'Essential safety features for safe room design',
        answer:
          'Install reinforced walls and ceilings, add secure locking systems with backup power, include emergency communication devices, ensure proper ventilation and air filtration, add first aid supplies and emergency rations, and incorporate monitoring systems for security alerts.',
      },
      {
        question: 'How to integrate technology into safe room security',
        answer:
          'Install smart locks with biometric access, add surveillance cameras with remote monitoring, use voice-activated emergency systems, incorporate smart lighting that activates during emergencies, add automated security protocols, and ensure backup power systems for all technology.',
      },
      {
        question: 'Safe room comfort and livability considerations',
        answer:
          'Add comfortable seating and bedding for extended stays, ensure proper temperature control, include entertainment options for mental wellbeing, add adequate lighting and ventilation, provide storage for emergency supplies, and create a psychologically calming environment.',
      },
      {
        question: 'Safe room concealment and access strategies',
        answer:
          'Use hidden entrances behind furniture or walls, create false walls or bookcase doors, ensure multiple access points if possible, add biometric or keypad entry systems, incorporate emergency exit options, and design for quick and easy access during crises.',
      },
      {
        question: 'Legal and building code considerations for safe rooms',
        answer:
          'Check local building codes for safe room requirements, consult with security professionals for design, ensure proper structural reinforcement, verify electrical and ventilation codes, consider insurance implications, and document all safety features for emergency responders.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'spa-retreat',
    name: 'Spa Retreat',
    faqs: [
      {
        question: 'How to create a luxurious spa experience on a budget',
        answer:
          'Focus on affordable upgrades like a deep soaking tub ($500-800), soft LED lighting ($200), plush towels and robes ($150), natural bamboo flooring ($400), and essential oils diffuser ($100). Add mirrors for ambiance, comfortable seating, and natural elements like plants and stones. Prioritize comfort and sensory elements over expensive fixtures.',
      },
      {
        question: 'Essential features for spa bathroom conversions',
        answer:
          'Install a rainfall showerhead with multiple settings, add a deep soaking tub or Japanese-style ofuro tub, include heated floors for comfort, provide ample storage for toiletries, add a vanity with proper lighting for grooming, incorporate sound systems for relaxation music, and ensure proper ventilation to manage humidity.',
      },
      {
        question: 'Best lighting design for spa-like relaxation spaces',
        answer:
          'Use dimmable LED lighting with warm color temperatures (2700-3000K), install under-cabinet lighting for practical tasks, add accent lighting on focal features like tubs or artwork, incorporate natural light through skylights or large windows, use candles or salt lamps for ambient glow, and avoid harsh overhead lighting that can be stressful.',
      },
      {
        question: 'How to incorporate natural elements into spa design',
        answer:
          'Use natural stone or bamboo for surfaces, incorporate live plants for air purification and ambiance, add natural fiber textiles like linen and cotton, include water features like small fountains, use essential oils and natural scents, incorporate wood elements and natural textures, and ensure good connection to outdoor natural elements through windows or indoor gardens.',
      },
      {
        question: 'Smart technology integration for modern spa retreats',
        answer:
          'Install smart tubs with programmable temperatures and jets, use voice-activated lighting and music systems, add smart mirrors with built-in displays, incorporate aromatherapy diffusers with app control, use smart towel warmers, add automated blinds for light control, and consider smart saunas or steam rooms with remote access.',
      },
      {
        question: 'Sustainable materials for eco-friendly spa design',
        answer:
          'Choose bamboo or reclaimed wood for cabinetry, use natural stone with responsible sourcing, select low-VOC paints and finishes, incorporate recycled glass or metal fixtures, use organic cotton for towels and robes, choose energy-efficient lighting and appliances, and use water-saving fixtures to minimize environmental impact.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'beauty-salon',
    name: 'Beauty Salon',
    // Before -> after loop built by scratchpad/room_pipeline.py: plain room
    // (GPT Image 2 Medium) morphing into the first style (Seedance 2.0 Mini
    // first->last frame), crossfading through two more layout-pinned edits
    // (Nano Banana 2) and back to the before. 720x720, 10.6s, muted.
    heroVideo:
      'https://media.easy-peasy.ai/landing/room-types/beauty-salon-hero.mp4',
    faqs: [
      {
        question: 'How to set up a professional beauty space',
        answer:
          'Create a dedicated workstation with proper lighting and seating, install professional mirrors with magnification, add storage for tools and products, ensure proper ventilation for chemical products, include a sink or wash station, add comfortable client seating, and organize products by type and frequency of use.',
      },
      {
        question: 'Essential lighting for makeup and hair styling stations',
        answer:
          'Install daylight-simulating LED lights (5000K) for accurate makeup application, add adjustable task lighting over workstations, use ring lights or softbox lights for makeup tutorials, incorporate vanity mirrors with built-in lighting, ensure even illumination without harsh shadows, and use dimmable controls for different services.',
      },
      {
        question: 'Storage solutions for beauty products and tools',
        answer:
          'Use drawer organizers for makeup and skincare products, install wall-mounted tool racks for brushes and appliances, add clear acrylic organizers for visibility, create designated zones for different product types, use magnetic strips for metal tools, incorporate under-sink storage for cleaning supplies, and label everything for quick access.',
      },
      {
        question: 'How to create a relaxing atmosphere in beauty spaces',
        answer:
          'Add soft background music and sound systems, use calming color palettes with soft neutrals, incorporate aromatherapy diffusers, add comfortable seating with cushions, include plants for natural ambiance, use soft lighting and candles, and create a clean, organized environment that promotes relaxation.',
      },
      {
        question: 'Ventilation and safety considerations for salons',
        answer:
          'Install exhaust fans for chemical fumes and hair product odors, ensure proper ventilation for spray products and treatments, add carbon filters for chemical absorption, maintain good air circulation to prevent mold, include fire safety equipment for heated tools, and ensure electrical safety for multiple appliances.',
      },
      {
        question: 'Smart technology integration for modern beauty salons',
        answer:
          'Add smart mirrors with built-in displays and lighting, use app-controlled lighting systems, incorporate smart inventory tracking for products, add voice-activated assistants for hands-free operation, use smart scheduling systems, include charging stations for devices, and consider smart security systems for valuable equipment.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'outdoor-kitchen',
    name: 'Outdoor Kitchen',
    faqs: [
      {
        question: 'How to design a functional outdoor kitchen layout',
        answer:
          'Plan around the "work triangle" between grill, prep area, and sink. Include weather-resistant countertops like granite or concrete, add adequate storage for utensils and supplies, incorporate proper drainage, ensure gas and electrical connections are safely installed, and leave enough counter space for food prep and serving.',
      },
      {
        question: 'Best materials for outdoor kitchen surfaces',
        answer:
          'Use marine-grade stainless steel for appliances and frames, choose natural stone or porcelain tile for countertops, select weather-resistant wood like teak or cedar for accents, use concrete or brick for structure, and ensure all materials can withstand UV exposure, rain, and temperature fluctuations in your climate.',
      },
      {
        question: 'Essential appliances for an outdoor kitchen',
        answer:
          'Start with a quality built-in grill as the centerpiece, add a refrigerator for cold storage, include a sink with running water, consider a pizza oven or smoker for versatility, add a beverage cooler or ice maker, and include adequate lighting for evening cooking and entertaining.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'apartment',
    name: 'Apartment / Studio Apartment',
    faqs: [
      {
        question: 'How to maximize space in a small apartment',
        answer:
          'Use multi-functional furniture like sofa beds and expandable tables, incorporate vertical storage with tall shelving, use mirrors to create the illusion of space, choose light colors for walls and furnishings, define zones with rugs and lighting rather than walls, and keep pathways clear for easy movement throughout the space.',
      },
      {
        question: 'Best room dividers for studio apartments',
        answer:
          'Use open bookshelves that provide storage and separation, try curtains or drapes for flexible privacy, consider folding screens for portable division, use different flooring or rugs to define zones, add a kitchen island as a natural divider, and use strategic furniture placement to create distinct living areas.',
      },
      {
        question: 'Storage solutions for small apartments',
        answer:
          'Install floating shelves on unused wall space, use under-bed storage containers, add over-door organizers, choose furniture with built-in storage like ottomans, use stackable containers in closets, incorporate hooks and pegboards for vertical organization, and consider ceiling-mounted storage for seasonal items.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'swimming-pool',
    name: 'Swimming Pool',
    faqs: [
      {
        question: 'How to design a pool area for maximum enjoyment',
        answer:
          'Consider pool shape and size based on yard dimensions, plan surrounding deck or patio space for lounging, add landscaping for privacy and aesthetics, include proper lighting for night swimming, plan for shade structures or umbrellas, ensure safety features like fencing and non-slip surfaces, and consider adding a hot tub or water features.',
      },
      {
        question: 'Pool landscaping and surround design ideas',
        answer:
          "Use tropical plants for a resort feel, add natural stone or travertine pavers for the pool deck, incorporate a fire pit or outdoor fireplace nearby, add a pergola or cabana for shade, use low-maintenance plants that don't shed into the pool, and create a cohesive design that connects the pool area to the rest of your outdoor space.",
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'front-yard',
    name: 'Front Yard',
    faqs: [
      {
        question: 'How to improve curb appeal with front yard design',
        answer:
          "Start with a well-maintained lawn or drought-tolerant groundcover, add a clear pathway to the front door, incorporate layered plantings with varying heights, add landscape lighting for evening appeal, consider a statement tree or focal point, update the mailbox and house numbers, and ensure the design complements your home's architectural style.",
      },
      {
        question: 'Low-maintenance front yard landscaping ideas',
        answer:
          'Use native plants adapted to your climate, incorporate mulch beds to reduce weeding, add gravel or decomposed granite pathways, consider artificial turf for green year-round, use drip irrigation for efficient watering, choose evergreen shrubs for year-round structure, and group plants with similar water needs together.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'deck',
    name: 'Deck',
    faqs: [
      {
        question: 'How to design a deck for outdoor living',
        answer:
          'Plan zones for dining, lounging, and cooking, choose between wood and composite decking materials, incorporate built-in seating or planters, add a pergola or awning for shade, include railing that complements your home style, plan for adequate lighting, and consider multi-level designs for visual interest and functional separation.',
      },
      {
        question: 'Best materials for a durable deck',
        answer:
          'Composite decking offers low maintenance and longevity, pressure-treated pine is budget-friendly, tropical hardwoods like ipe are naturally durable, cedar and redwood resist rot naturally, PVC decking is fully waterproof, and aluminum framing prevents structural decay. Consider your climate, budget, and maintenance preferences when choosing.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'tiny-house',
    name: 'Tiny House / Micro Home',
    faqs: [
      {
        question: 'How to maximize space in a tiny house',
        answer:
          'Use loft spaces for sleeping areas, install fold-down furniture like Murphy beds and drop-leaf tables, choose compact appliances designed for small spaces, build custom storage into every available nook, use sliding doors instead of swing doors, incorporate multi-functional furniture, and keep the color palette light to feel more spacious.',
      },
      {
        question: 'Essential design principles for tiny living',
        answer:
          'Prioritize vertical space with tall ceilings and lofts, keep the layout open and minimal, use large windows to bring in natural light, choose dual-purpose items wherever possible, maintain ruthless organization systems, incorporate outdoor living space as an extension of your home, and design around your daily routines for maximum efficiency.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'dental-office',
    name: 'Dental Office',
    faqs: [
      {
        question: 'How to design a welcoming dental office',
        answer:
          'Create a calming reception area with comfortable seating, use soothing color palettes like blues and greens, ensure proper lighting in treatment rooms, design efficient workflow between reception, treatment, and sterilization areas, incorporate patient entertainment like TVs on ceilings, and add natural elements like plants to reduce patient anxiety.',
      },
      {
        question: 'Dental office layout and workflow optimization',
        answer:
          'Design a logical patient flow from reception to treatment to checkout, position sterilization areas centrally for easy access, ensure treatment rooms have proper plumbing and electrical, include dedicated spaces for X-ray and lab work, provide private consultation rooms, and plan storage for dental supplies and equipment.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'barbershop',
    name: 'Barbershop',
    faqs: [
      {
        question: 'How to design a modern barbershop interior',
        answer:
          'Create a strong visual identity with a cohesive theme, invest in quality barber chairs as the centerpiece, install proper mirrors with good lighting at each station, use industrial or vintage elements for character, add a comfortable waiting area, display products attractively, and include entertainment like TVs or a sound system for the social atmosphere.',
      },
      {
        question: 'Essential layout considerations for barbershops',
        answer:
          'Space stations at least 5 feet apart for comfortable working, position the reception and retail area near the entrance, ensure good ventilation for product fumes, add adequate electrical outlets at each station, include a wash station area, provide storage for tools and towels, and create a distinct waiting zone separate from the cutting floor.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'tattoo-studio',
    name: 'Tattoo Studio',
    faqs: [
      {
        question: 'How to design a professional tattoo studio',
        answer:
          'Prioritize cleanliness with easy-to-sanitize surfaces, ensure bright adjustable lighting at each workstation, create private or semi-private tattooing areas, display portfolio artwork on walls, use comfortable reclining chairs for clients, maintain separate clean and contaminated zones, and incorporate proper ventilation for ink and cleaning product fumes.',
      },
      {
        question: 'Creating the right atmosphere in a tattoo parlor',
        answer:
          "Balance edgy aesthetics with a welcoming environment, showcase artist portfolios and flash art on walls, use ambient lighting in common areas with task lighting at stations, add a quality sound system for music, create a comfortable consultation area, display awards and certifications prominently, and design a space that reflects your studio's unique brand and artistic identity.",
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'daycare',
    name: 'Daycare / Childcare Center',
    faqs: [
      {
        question: 'How to design a safe and stimulating daycare space',
        answer:
          'Use bright, cheerful colors that stimulate learning, ensure all furniture has rounded edges, install soft flooring like rubber mats or carpet, create distinct zones for play, rest, and meals, add child-height shelving for accessible storage, ensure clear sightlines for supervision, and include both indoor and outdoor play areas.',
      },
      {
        question: 'Age-appropriate zones in childcare design',
        answer:
          'Create separate areas for infants, toddlers, and preschoolers, include a quiet nap room with individual cots, design an art and messy play area with washable surfaces, add a reading nook with soft seating, include a gross motor play area, plan a secure outdoor playground, and ensure age-appropriate bathroom facilities are easily accessible.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'bakery',
    name: 'Bakery',
    faqs: [
      {
        question: 'How to design an inviting bakery interior',
        answer:
          'Create a warm, welcoming atmosphere with rustic or artisanal elements, design an attractive display case as the focal point, use warm lighting to enhance baked goods appearance, add seating for customers who want to stay, incorporate open kitchen elements so customers can see baking in action, and choose a color palette that complements your brand.',
      },
      {
        question: 'Bakery layout for efficient workflow',
        answer:
          'Separate front-of-house display from back-of-house production, position ovens and prep areas for logical workflow, ensure adequate ventilation for heat and aromas, include proper storage for ingredients and supplies, design the counter area for efficient order-taking and packaging, and plan for both retail and potential wholesale operations.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'pharmacy',
    name: 'Pharmacy',
    faqs: [
      {
        question: 'How to design a modern pharmacy layout',
        answer:
          'Create a clear customer flow from entrance to counter to exit, position the dispensing counter for privacy and efficiency, design retail shelving for easy product browsing, include a private consultation room for patient discussions, ensure proper medication storage with climate control, add clear signage and wayfinding, and create a welcoming health and wellness retail area.',
      },
      {
        question: 'Pharmacy design for customer experience',
        answer:
          'Use open, well-lit layouts for easy navigation, group products logically by health category, add digital displays for health information, create a comfortable waiting area near the dispensing counter, include self-service kiosks for prescriptions, ensure ADA accessibility throughout, and design the space to feel professional yet approachable.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'pilates-studio',
    name: 'Pilates / Barre Studio',
    faqs: [
      {
        question: 'How to design a functional Pilates or barre studio',
        answer:
          'Install full-length mirrors along at least one wall, use sprung or semi-sprung flooring for joint protection, mount ballet barres at proper height, ensure adequate spacing between reformers or equipment, add proper ventilation and climate control, include storage for props like resistance bands and balls, and create a calming aesthetic with neutral tones.',
      },
      {
        question: 'Equipment layout for boutique fitness studios',
        answer:
          'Space reformers or mats at least 3 feet apart, position the instructor area for maximum visibility, add wall-mounted or ceiling-mounted TRX anchors if needed, include a sound system for class music, ensure electrical outlets for any powered equipment, create a separate area for stretching and warm-up, and design changing rooms with showers and lockers.',
      },
      ...commonFaqs,
    ],
  },
  {
    slug: 'campervan',
    name: 'Van Interior / Campervan',
    // Before -> after loop built by scratchpad/room_pipeline.py: plain room
    // (GPT Image 2 Medium) morphing into the first style (Seedance 2.0 Mini
    // first->last frame), crossfading through two more layout-pinned edits
    // (Nano Banana 2) and back to the before. 720x720, 10.6s, muted.
    heroVideo:
      'https://media.easy-peasy.ai/landing/room-types/campervan-hero.mp4',
    faqs: [
      {
        question: 'How to design a functional campervan interior',
        answer:
          'Prioritize a logical layout with sleeping, cooking, and living zones, use lightweight materials to stay within weight limits, incorporate a fixed or convertible bed platform with under-bed storage, design a compact kitchenette with sink, stove, and mini fridge, add adequate 12V lighting and USB charging, and ensure proper ventilation with roof vents or windows.',
      },
      {
        question: 'Storage solutions for van life',
        answer:
          'Build custom cabinetry to fit every contour of the van, use overhead cabinets for lightweight items, incorporate slide-out drawers under the bed, add magnetic strips for kitchen utensils, use cargo nets and bungee organizers on walls, include a garage area in the back for outdoor gear, and use modular storage containers that can be removed when needed.',
      },
      ...commonFaqs,
    ],
  },
];
